import React, { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';
import './GenPro.css';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const GenPro = () => {
    const [vibe, setVibe] = useState('energetic');
    const [customVibe, setCustomVibe] = useState('');
    const [count, setCount] = useState(1);
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [logs, setLogs] = useState([]);
    const [taskStatuses, setTaskStatuses] = useState({}); // Track individual song statuses

    // Image Generation State
    const [imageCandidates, setImageCandidates] = useState({}); // { task_id: [candidates] }
    const [selectedImages, setSelectedImages] = useState({}); // { task_id: candidate_index }
    const [isGeneratingImages, setIsGeneratingImages] = useState(false);
    const [imageGenProgress, setImageGenProgress] = useState('');
    const [imagePollInterval, setImagePollInterval] = useState(null);
    
    // Playlist Song Selection State
    const [playlistSongs, setPlaylistSongs] = useState([]); // Selected and ordered songs for playlist
    const [playlistCoverPrompt, setPlaylistCoverPrompt] = useState(`🎨 AI will automatically generate varied background styles including:

• Vintage Korean Book - cream, beige, aged paper texture
• Elegant Floral Frame - pink, lavender, watercolor flowers
• Sunset Gradient - coral, orange, purple gradient
• Classic Vinyl Record - burgundy, gold, retro patterns
• Autumn Leaves - orange, golden yellow, maple leaves
• Night Sky - navy, purple, stars and moon
• Traditional Korean - hanbok colors, dancheong patterns
• Ocean Waves - teal, aqua, coastal theme

Each generation picks a random style for variety.
Text will be burned in with dark brown color for readability.`);

    // New State for account info
    const [userInfo, setUserInfo] = useState(null);

    // Folder Creation State
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    
    // Modal State
    const [modal, setModal] = useState({ isOpen: false, type: 'info', title: '', message: '', onConfirm: null });

    useEffect(() => {
        fetchFolders();
        fetchUserInfo();
    }, []);

    // Polling Logic
    useEffect(() => {
        let timer;
        let isPolling = false;

        const poll = async () => {
            if (isPolling) return;
            
            const pendingIds = Object.keys(taskStatuses).filter(id => {
                const s = taskStatuses[id];
                // Don't poll for lyrics-only tasks (they don't exist in DB)
                if (s && s.status === 'lyrics_complete') return false;
                return s && s.drive_status !== 'uploaded' && s.status !== 'error';
            });
            
            if (pendingIds.length === 0) return;

            isPolling = true;
            try {
                const res = await fetch(`${API_URL}/check-music-status?ids=${pendingIds.join(',')}`);
                if (!res.ok) throw new Error("Status check failed");
                const results = await res.json();
                
                setTaskStatuses(prev => {
                    const next = { ...prev };
                    let changed = false;
                    results.forEach(item => {
                        if (item && item.id && JSON.stringify(next[item.id]) !== JSON.stringify(item)) {
                            next[item.id] = item;
                            changed = true;
                            if (item.drive_status === 'uploaded') {
                                setLogs(l => [`✅ Uploaded: ${item.title || item.id}`, ...l]);
                            }
                        }
                    });
                    return changed ? next : prev;
                });
            } catch (err) {
                console.error("Polling error", err);
            } finally {
                isPolling = false;
                // Schedule next poll ONLY if still mounted and has pending tasks
                timer = setTimeout(poll, 4000);
            }
        };

        const initialPending = Object.keys(taskStatuses).filter(id => {
            const s = taskStatuses[id];
            // Don't poll for lyrics-only tasks (they don't exist in DB)
            if (s && s.status === 'lyrics_complete') return false;
            return s && s.drive_status !== 'uploaded' && s.status !== 'error';
        });

        console.log('🔄 Polling effect triggered. Task count:', Object.keys(taskStatuses).length);
        console.log('🔄 Pending tasks for polling:', initialPending);

        if (initialPending.length > 0) {
            console.log('✅ Starting polling in 2 seconds...');
            timer = setTimeout(poll, 2000);
        } else {
            console.log('⚠️ No pending tasks to poll');
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [taskStatuses]); // Re-run effect when taskStatuses changes

    const fetchUserInfo = async () => {
        try {
            const res = await fetch(`${API_URL}/user-info`);
            const data = await res.json();
            if (data.email) setUserInfo(data);
        } catch (err) {
            console.error("Failed to fetch user info", err);
        }
    };

    const fetchFolders = async () => {
        try {
            const res = await fetch(`${API_URL}/drive/folders`);
            const data = await res.json();
            if (data.folders) {
                // Sort folders alphabetically
                const sorted = data.folders.sort((a, b) => a.name.localeCompare(b.name));
                setFolders(sorted);
            }
        } catch (err) {
            console.error("Failed to fetch folders", err);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        try {
            const res = await fetch(`${API_URL}/drive/folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    name: newFolderName,
                    parent_id: selectedFolder || null // Create inside the currently selected folder if one exists
                })
            });
            const data = await res.json();
            if (data.status === 'created') {
                await fetchFolders();
                setSelectedFolder(data.id);
                setNewFolderName('');
                setIsCreatingFolder(false);
            }
        } catch (err) {
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to create folder', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        }
    };

    const [activeSubTab, setActiveSubTab] = useState('playlist'); // 'playlist', 'config'
    const [jurySongs, setJurySongs] = useState([]);

    const [isRunningJury, setIsRunningJury] = useState(false);
    
    // Session management state
    const [sessions, setSessions] = useState([]);
    const [showSessionModal, setShowSessionModal] = useState(false);
    
    // Configuration state
    const [config, setConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(false);
    
    // Playlist pipeline state
    const [playlistStage, setPlaylistStage] = useState('intro'); // 'intro', 'lyrics', 'music', 'jury', 'images', 'video', 'preview', 'metadata', 'final-preview', 'upload'
    const [playlistConfig, setPlaylistConfig] = useState({
        vibe: 'energetic',
        numSongs: 5,
        language: 'Korean',
        gender: null,  // null, 'male', or 'female'
        songOrder: []
    });
    const [playlistMetadata, setPlaylistMetadata] = useState({
        title: '',
        description: '',
        timestamps: []
    });
    const [sessionId, setSessionId] = useState(null);

    const runJuryFilter = async () => {
        if (isRunningJury) return;
        setIsRunningJury(true);
        try {
            const res = await fetch(`${API_URL}/jury/run-filter`, { method: 'POST' });
            const data = await res.json();
            if (res.ok) {
                setModal({ isOpen: true, type: 'success', title: 'Analysis Complete', message: `Jury analysis complete! Processed ${data.processed} songs.`, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                fetchJurySongs();
            }
        } catch (err) {
            console.error("Failed to run jury filter", err);
        } finally {
            setIsRunningJury(false);
        }
    };

    const handleNewSession = async () => {
        setModal({
            isOpen: true,
            type: 'confirm',
            title: 'Start New Session?',
            message: 'This will archive your current work (you can load it back later).',
            showCancel: true,
            onConfirm: async () => {
                setModal({ ...modal, isOpen: false });
                await executeNewSession();
            },
            onClose: () => setModal({ ...modal, isOpen: false })
        });
    };
    
    const executeNewSession = async () => {
        
        try {
            // Clear image polling interval before resetting
            if (imagePollInterval) {
                clearInterval(imagePollInterval);
                setImagePollInterval(null);
            }
            
            const res = await fetch(`${API_URL}/sessions/new`, { method: 'POST' });
            const data = await res.json();
            if (res.ok) {
                // Store session ID
                setSessionId(data.session_id);
                // Clear UI state
                setTaskStatuses({});
                setJurySongs([]);
                setLogs([]);
                setProgress(0);
                setStatusMessage('');
                setImageCandidates({});
                setSelectedImages({});
                setIsGeneratingImages(false);
                setImageGenProgress('');
                setModal({ isOpen: true, type: 'success', title: 'Success', message: 'New session started! Previous work archived.', onConfirm: () => setModal({ ...modal, isOpen: false }) });
            }
        } catch (err) {
            console.error("Failed to create new session", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to create new session', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        }
    };

    const fetchSessions = async () => {
        try {
            const res = await fetch(`${API_URL}/sessions`);
            const data = await res.json();
            setSessions(data.sessions || []);
        } catch (err) {
            console.error("Failed to fetch sessions", err);
        }
    };

    const loadSession = async (sessionId) => {
        try {
            const res = await fetch(`${API_URL}/sessions/${sessionId}/load`, { method: 'POST' });
            if (res.ok) {
                // Refresh UI
                setTaskStatuses({});
                fetchJurySongs();
                setShowSessionModal(false);
                setModal({ isOpen: true, type: 'success', title: 'Success', message: 'Session loaded!', onConfirm: () => setModal({ ...modal, isOpen: false }) });
            }
        } catch (err) {
            console.error("Failed to load session", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to load session', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        }
    };

    const createPlaylist = async () => {
        setModal({
            isOpen: true,
            type: 'confirm',
            title: 'Create YouTube Playlist?',
            message: 'This will:\n1. Generate video images\n2. Create MP4 videos\n3. Generate metadata\n4. Upload to YouTube\n\nThis may take several minutes.',
            showCancel: true,
            onConfirm: async () => {
                setModal({ ...modal, isOpen: false });
                await executeCreatePlaylist();
            },
            onClose: () => setModal({ ...modal, isOpen: false })
        });
    };
    
    const executeCreatePlaylist = async () => {
        
        try {
            const res = await fetch(`${API_URL}/pipeline/create-playlist`, { method: 'POST' });
            const data = await res.json();
            
            if (data.error) {
                setModal({ isOpen: true, type: 'error', title: 'Error', message: data.error, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                return;
            }
            
            if (res.ok) {
                setModal({ isOpen: true, type: 'success', title: 'Success', message: `Playlist creation started for ${data.song_count} songs! Check backend logs for progress.`, onConfirm: () => setModal({ ...modal, isOpen: false }) });
            }
        } catch (err) {
            console.error("Failed to create playlist", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to start playlist creation', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        }
    };

    const fetchConfig = async () => {
        setConfigLoading(true);
        try {
            const res = await fetch(`${API_URL}/config`);
            const data = await res.json();
            setConfig(data);
        } catch (err) {
            console.error("Failed to fetch config", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to load configuration', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        } finally {
            setConfigLoading(false);
        }
    };

    const saveConfig = async () => {
        setConfigLoading(true);
        try {
            const res = await fetch(`${API_URL}/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            });
            const data = await res.json();
            if (res.ok) {
                setModal({ isOpen: true, type: 'success', title: 'Success', message: 'Configuration saved successfully!', onConfirm: () => setModal({ ...modal, isOpen: false }) });
            } else {
                setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to save configuration', onConfirm: () => setModal({ ...modal, isOpen: false }) });
            }
        } catch (err) {
            console.error("Failed to save config", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to save configuration', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        } finally {
            setConfigLoading(false);
        }
    };

    const resetConfig = async () => {
        setModal({
            isOpen: true,
            type: 'confirm',
            title: 'Reset Configuration?',
            message: 'Reset all configuration to defaults?',
            showCancel: true,
            onConfirm: async () => {
                setModal({ ...modal, isOpen: false });
                await executeResetConfig();
            },
            onClose: () => setModal({ ...modal, isOpen: false })
        });
    };
    
    const executeResetConfig = async () => {
        
        setConfigLoading(true);
        try {
            const res = await fetch(`${API_URL}/config/reset`, { method: 'POST' });
            if (res.ok) {
                setModal({ isOpen: true, type: 'success', title: 'Success', message: 'Configuration reset to defaults', onConfirm: () => setModal({ ...modal, isOpen: false }) });
                fetchConfig();
            }
        } catch (err) {
            console.error("Failed to reset config", err);
            setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to reset configuration', onConfirm: () => setModal({ ...modal, isOpen: false }) });
        } finally {
            setConfigLoading(false);
        }
    };

    const updateConfigField = (section, field, value) => {
        setConfig(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const fetchJurySongs = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/jury/songs`);
            const data = await res.json();
            setJurySongs(data.songs || []);
        } catch (err) {
            console.error("Failed to fetch jury songs", err);
        }
    }, []);

    // Removed jury tab auto-fetch - jury is now part of YouTube Playlist workflow

    const handleJuryAction = async (id, action, stage = null) => {
        try {
            const res = await fetch(`${API_URL}/jury/action`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, action, stage })
            });
            if (res.ok) {
                fetchJurySongs();
            }
        } catch (err) {
            console.error("Failed to perform jury action", err);
        }
    };

    const handleRateSong = async (id, rating) => {
        try {
            // Optimistic update - immediately update UI
            setJurySongs(prev => prev.map(song => {
                if (song.task_id === id) {
                    return { 
                        ...song, 
                        user_rating: rating,
                        // If dislike, immediately demote from Agent 3
                        pass_a3: rating === 'dislike' ? 0 : song.pass_a3
                    };
                }
                return song;
            }));

            const res = await fetch(`${API_URL}/rate-song`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, rating })
            });
            
            if (res.ok) {
                // Wait a moment for backend re-evaluation to complete
                setTimeout(() => {
                    fetchJurySongs();
                }, 1500);
            }
        } catch (err) {
            console.error("Failed to rate song", err);
            // Revert optimistic update on error
            fetchJurySongs();
        }
    };

    const startGeneration = async (overrideVibe, overrideCount, overrideFolder) => {
        if (isGenerating) return;
        
        setIsGenerating(true);
        setProgress(5);
        setLogs([]);
        setTaskStatuses({}); // Reset task tracking
        setStatusMessage("Initializing Pro workflow...");

        // Use override parameters if provided (from YouTube Playlist), otherwise use state
        const finalVibe = overrideVibe ? (overrideVibe === 'custom' ? customVibe : overrideVibe) : (vibe === 'custom' ? customVibe : vibe);
        const finalCount = overrideCount !== undefined ? parseInt(overrideCount) : parseInt(count);
        const finalFolder = overrideFolder !== undefined ? overrideFolder : selectedFolder;

        try {
            const response = await fetch(`${API_URL}/gen-pro`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    vibe: finalVibe,
                    count: finalCount,
                    folder_id: finalFolder || null
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n').filter(l => l.trim());

                for (const line of lines) {
                    try {
                        const update = JSON.parse(line);
                        setProgress(update.percent || progress);
                        setStatusMessage(update.message);
                        setLogs(prev => [update.message, ...prev.slice(0, 19)]); // Keep last 20 logs, newest first

                        if (update.status === 'complete') {
                            setIsGenerating(false);
                            // Initialize task tracking for polling
                            const initialStatuses = {};
                            if (update.data && update.data.task_ids) {
                                update.data.task_ids.forEach(id => {
                                    initialStatuses[id] = { id, status: 'submitted', drive_status: 'pending' };
                                });
                                setTaskStatuses(initialStatuses);
                            }
                        }
                        if (update.status === 'error') {
                            setIsGenerating(false);
                            setStatusMessage(update.message);
                            setLogs(prev => [`❌ ERROR: ${update.message}`, ...prev]);
                            return; // Stop processing this stream
                        }
                    } catch (e) {
                        console.error("Error parsing stream line", e);
                    }
                }
            }
        } catch (err) {
            setStatusMessage("Error: " + err.message);
            setIsGenerating(false);
        }
    };

    return (
        <div className="gen-pro-wrapper" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', minHeight: '100vh', paddingBottom: '40px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '2rem', background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Gen Pro</h1>
                    <p style={{ color: '#888', margin: '5px 0 0 0' }}>Industrial-grade music generation & filtering</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', background: '#111', padding: '6px', borderRadius: '12px' }}>
                    <button 
                        onClick={() => setActiveSubTab('playlist')}
                        style={{ 
                            padding: '10px 20px', 
                            borderRadius: '8px', 
                            border: 'none', 
                            background: activeSubTab === 'playlist' ? '#222' : 'transparent',
                            color: activeSubTab === 'playlist' ? '#FF9068' : '#666',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s'
                        }}
                    >
                        🎬 YouTube Playlist
                    </button>
                    <button 
                        onClick={() => {
                            setActiveSubTab('config');
                            if (!config) fetchConfig();
                        }}
                        style={{ 
                            padding: '10px 20px', 
                            borderRadius: '8px', 
                            border: 'none', 
                            background: activeSubTab === 'config' ? '#222' : 'transparent',
                            color: activeSubTab === 'config' ? '#FF9068' : '#666',
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            transition: 'all 0.2s'
                        }}
                    >
                        ⚙️ Configuration
                    </button>
                </div>
            </div>

            {/* Generator tab removed - all functionality moved to YouTube Playlist workflow */}
            {false && activeSubTab === 'generator' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '30px' }}>
                    {/* LEFT: CONFIGURATION */}
                    <div className="gen-pro-config" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ background: '#141414', padding: '25px', borderRadius: '16px', border: '1px solid #333', flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#eee' }}>1. Configure Batch</h3>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button 
                                        onClick={handleNewSession}
                                        style={{ 
                                            padding: '6px 12px', 
                                            borderRadius: '6px', 
                                            border: '1px solid #4CAF50', 
                                            background: 'transparent',
                                            color: '#4CAF50',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold'
                                        }}
                                        title="Archive current work and start fresh"
                                    >
                                        🔄 New
                                    </button>
                                    <button 
                                        onClick={() => {
                                            fetchSessions();
                                            setShowSessionModal(true);
                                        }}
                                        style={{ 
                                            padding: '6px 12px', 
                                            borderRadius: '6px', 
                                            border: '1px solid #666', 
                                            background: 'transparent',
                                            color: '#666',
                                            cursor: 'pointer',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold'
                                        }}
                                        title="Load past work"
                                    >
                                        📂 Load
                                    </button>
                                </div>
                            </div>
                            
                            <div className="form-group" style={{ marginBottom: '25px' }}>
                                <label style={{ display: 'block', marginBottom: '10px', color: '#aaa', fontSize: '0.9rem', fontWeight: '600' }}>Music Vibe / Style</label>
                                <select 
                                    value={vibe} 
                                    onChange={(e) => setVibe(e.target.value)} 
                                    style={{ width: '100%', padding: '14px', background: '#1f1f1f', border: '1px solid #444', color: '#fff', borderRadius: '10px', fontSize: '1rem', outline: 'none' }}
                                >
                                    <option value="energetic">Energetic (Pop/Rock/Dance)</option>
                                    <option value="emotional">Emotional (Ballad/Soul)</option>
                                    <option value="chill">Chill (Lo-fi/Jazz)</option>
                                    <option value="mysterious">Mysterious / Cinematic</option>
                                    <option value="custom">Custom Vibe...</option>
                                </select>
                            </div>

                            {vibe === 'custom' && (
                                <div className="form-group" style={{ marginBottom: '25px' }}>
                                    <input 
                                        type="text" 
                                        placeholder="Describe the vibe (e.g. Funky Summer Disco)" 
                                        value={customVibe} 
                                        onChange={(e) => setCustomVibe(e.target.value)}
                                        style={{ width: '100%', padding: '14px', background: '#1f1f1f', border: '1px solid #444', color: '#fff', borderRadius: '10px', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                                    />
                                </div>
                            )}

                            <div className="form-group" style={{ marginBottom: '30px' }}>
                                <label style={{ display: 'block', marginBottom: '10px', color: '#aaa', fontSize: '0.9rem', fontWeight: '600' }}>Number of Songs</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <input 
                                        type="range" min="1" max="100" 
                                        value={count} 
                                        onChange={(e) => setCount(e.target.value)}
                                        style={{ flex: 1, accentColor: '#FF4B1F', height: '6px' }}
                                    />
                                    <input 
                                        type="number"
                                        min="1"
                                        max="100"
                                        value={count}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value);
                                            if (!isNaN(val)) setCount(Math.min(100, Math.max(1, val)));
                                        }}
                                        style={{ 
                                            width: '55px', 
                                            background: '#000', 
                                            border: '1px solid #444', 
                                            color: '#FF4B1F', 
                                            borderRadius: '6px', 
                                            padding: '6px 4px', 
                                            fontSize: '1.3rem', 
                                            fontWeight: '900', 
                                            textAlign: 'center',
                                            outline: 'none',
                                            boxShadow: 'inset 0 0 5px rgba(0,0,0,0.5)'
                                        }}
                                    />
                                </div>
                            </div>

                            <h3 style={{ marginTop: '40px', marginBottom: '20px', fontSize: '1.1rem', color: '#eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                <span style={{ whiteSpace: 'nowrap', fontWeight: '700' }}>2. Destination</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 1, minWidth: 0 }}>
                                    {userInfo && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', opacity: 0.7, minWidth: 0 }}>
                                            {userInfo.picture && <img src={userInfo.picture} alt="" style={{ width: '16px', height: '16px', borderRadius: '50%' }} />}
                                            <span style={{ fontSize: '0.6rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70px' }}>{userInfo.email}</span>
                                        </div>
                                    )}
                                    <span style={{ fontSize: '0.6rem', color: folders.length > 0 ? '#1DB954' : '#ff4444', background: 'rgba(0,0,0,0.3)', padding: '3px 8px', borderRadius: '20px', border: '1px solid currentColor', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        {folders.length > 0 ? `${folders.length} Folders` : 'No Folders'}
                                    </span>
                                    <a 
                                        href={`${API_URL}/auth/login`} 
                                        style={{ fontSize: '0.6rem', color: '#FF4B1F', textDecoration: 'none', border: '1px solid #FF4B1F', padding: '3px 8px', borderRadius: '20px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                        title="Switch or Re-authorize Account"
                                    >
                                        Reconnect
                                    </a>
                                </div>
                            </h3>
                            
                            <div className="form-group" style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select 
                                        value={selectedFolder} 
                                        onChange={(e) => setSelectedFolder(e.target.value)}
                                        style={{ flex: 1, padding: '14px', background: '#1f1f1f', border: '1px solid #444', color: '#fff', borderRadius: '10px', fontSize: '1rem', outline: 'none' }}
                                    >
                                        <option value="">Root Directory</option>
                                        {folders.map(f => (
                                            <option key={f.id} value={f.id}>{f.name}</option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={fetchFolders}
                                        style={{ padding: '0 12px', background: '#222', border: '1px solid #444', color: '#fff', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.2s' }}
                                        title="Refresh Folders"
                                    >
                                        🔄
                                    </button>
                                    <button 
                                        onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                                        style={{ padding: '0 18px', background: isCreatingFolder ? '#444' : '#222', border: '1px solid #444', color: '#fff', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold' }}
                                        title="New Folder"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {isCreatingFolder && (
                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', padding: '15px', background: '#000', borderRadius: '10px', border: '1px solid #333' }}>
                                    <input 
                                        type="text" 
                                        placeholder="New folder name..." 
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        style={{ flex: 1, padding: '10px 14px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '0.9rem', outline: 'none' }}
                                    />
                                    <button 
                                        onClick={handleCreateFolder}
                                        style={{ padding: '10px 20px', background: '#1DB954', border: 'none', color: '#000', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', fontSize: '0.85rem' }}
                                    >
                                        Create
                                    </button>
                                </div>
                            )}

                            <button 
                                onClick={startGeneration} 
                                disabled={isGenerating}
                                className="btn-primary"
                                style={{ 
                                    width: '100%', 
                                    marginTop: '40px', 
                                    padding: '20px', 
                                    fontSize: '1.2rem',
                                    opacity: isGenerating ? 0.6 : 1,
                                    cursor: isGenerating ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '12px',
                                    borderRadius: '12px',
                                    background: isGenerating ? '#333' : 'var(--accent-gradient)'
                                }}
                            >
                                {isGenerating ? (
                                    <>
                                        <span className="spinner"></span>
                                        Generating Batch...
                                    </>
                                ) : "🚀 Start Pro Generation"}
                            </button>
                        </div>
                    </div>

                    {/* RIGHT: PROGRESS & LOGS */}
                    <div className="gen-pro-status" style={{ display: 'flex', flexDirection: 'column' }}>
                        <div style={{ background: '#141414', padding: '25px', borderRadius: '16px', border: '1px solid #333', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.2rem', color: '#eee' }}>3. Live Status</h3>
                            
                            {!isGenerating && logs.length === 0 ? (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#555', textAlign: 'center', padding: '40px' }}>
                                    <span style={{ fontSize: '4.5rem', marginBottom: '25px', filter: 'grayscale(0.5)' }}>💤</span>
                                    <h4 style={{ margin: '0 0 12px 0', color: '#aaa', fontSize: '1.2rem' }}>System Idle</h4>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#666', lineHeight: '1.5' }}>
                                        Configure your batch on the left<br/>
                                        and hit "Start Pro Generation" to begin the automation.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <div className="progress-section" style={{ marginBottom: '25px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9rem' }}>
                                            <span style={{ color: '#FF9068', fontWeight: 'bold' }}>{statusMessage}</span>
                                            <span style={{ color: '#888' }}>{progress}%</span>
                                        </div>
                                        <div style={{ height: '8px', background: '#222', borderRadius: '4px', overflow: 'hidden' }}>
                                            <div style={{ 
                                                width: `${progress}%`, 
                                                height: '100%', 
                                                background: 'var(--accent-gradient)', 
                                                transition: 'width 0.4s ease',
                                                boxShadow: '0 0 10px var(--accent-glow)'
                                            }}></div>
                                        </div>
                                    </div>

                                    <div style={{ 
                                        flex: 1,
                                        background: '#090909', 
                                        padding: '15px', 
                                        borderRadius: '10px', 
                                        border: '1px solid #222',
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                        overflowY: 'auto',
                                        maxHeight: '200px',
                                        marginBottom: '20px'
                                    }}>
                                        {logs.map((log, i) => (
                                            <div key={i} style={{ marginBottom: '6px', color: i === 0 ? '#1DB954' : '#666', borderLeft: i === 0 ? '2px solid #1DB954' : 'none', paddingLeft: i === 0 ? '8px' : '0' }}>
                                                <span style={{ opacity: 0.5, marginRight: '8px' }}>[{new Date().toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}]</span>
                                                {log}
                                            </div>
                                        ))}
                                        {isGenerating && <div className="blink-cursor">_</div>}
                                    </div>

                                    {/* ACTIVE TASKS LIST */}
                                    {Object.keys(taskStatuses).length > 0 && (
                                        <div className="tasks-list" style={{ flex: 1, borderTop: '1px solid #333', paddingTop: '20px', overflowY: 'auto', maxHeight: '300px' }}>
                                            <h4 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#aaa', display: 'flex', justifyContent: 'space-between' }}>
                                                Processing Queue
                                                <span style={{ fontSize: '0.7rem' }}>
                                                    {Object.values(taskStatuses).filter(t => t.drive_status === 'uploaded').length} / {Object.keys(taskStatuses).length} Done
                                                </span>
                                            </h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {Object.values(taskStatuses).map(task => (
                                                    <div key={task.id} style={{ background: '#111', padding: '10px 15px', borderRadius: '8px', border: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderLeft: task.status === 'failed' ? '3px solid #ff4444' : '1px solid #222', gap: '15px' }}>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: task.status === 'failed' ? '#ff4444' : '#eee', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={task.title}>
                                                                {task.title || 'Untitled Song'}
                                                            </div>
                                                            <div style={{ fontSize: '0.7rem', color: '#666' }}>ID: {task.id.slice(0, 8)}...</div>
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
                                                            <div style={{ 
                                                                fontSize: '0.7rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '4px', 
                                                                background: task.status === 'complete' ? 'rgba(29, 185, 84, 0.1)' : (task.status === 'failed' ? 'rgba(255, 68, 68, 0.1)' : 'rgba(255, 144, 104, 0.1)'),
                                                                color: task.status === 'complete' ? '#1DB954' : (task.status === 'failed' ? '#ff4444' : '#FF9068'),
                                                                border: `1px solid ${task.status === 'complete' ? '#1DB95433' : (task.status === 'failed' ? '#ff444433' : '#FF906833')}`
                                                            }}>
                                                                {task.status.toUpperCase()}
                                                            </div>
                                                            <div style={{ 
                                                                fontSize: '0.7rem', 
                                                                padding: '3px 8px', 
                                                                borderRadius: '4px', 
                                                                background: task.drive_status === 'uploaded' ? 'rgba(29, 185, 84, 0.2)' : (task.drive_status === 'uploading' ? 'rgba(255, 144, 104, 0.2)' : '#222'),
                                                                color: task.drive_status === 'uploaded' ? '#1DB954' : (task.drive_status === 'uploading' ? '#FF9068' : '#888'),
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {task.drive_status === 'uploaded' ? 'DRIVE ✓' : (task.drive_status === 'uploading' ? 'UPLOADING...' : 'WAITING')}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!isGenerating && Object.keys(taskStatuses).length > 0 && Object.values(taskStatuses).every(t => t.drive_status === 'uploaded') && (
                                        <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(29, 185, 84, 0.1)', borderRadius: '8px', border: '1px solid rgba(29, 185, 84, 0.3)', color: '#1DB954', textAlign: 'center' }}>
                                            <strong>✅ All Songs Uploaded!</strong>
                                            <p style={{ margin: '5px 0 0', fontSize: '0.8rem' }}>Batch complete. Check your Google Drive folder.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Music Jury tab removed - filtering integrated into YouTube Playlist workflow */}
            {false && activeSubTab === 'jury' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* JURY HEADER WITH ACTIONS */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <div style={{ background: 'rgba(255, 144, 104, 0.05)', border: '1px solid rgba(255, 144, 104, 0.2)', padding: '15px 25px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '20px', flex: 1 }}>
                            <div style={{ fontSize: '2rem' }}>💡</div>
                            <div>
                                <h4 style={{ margin: 0, color: '#FF9068', fontSize: '0.95rem' }}>How the Music Jury Works</h4>
                                <p style={{ margin: '5px 0 0 0', color: '#888', fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    Songs flow from left to right. <b>Sound Engineer</b> filters technical quality, <b>Market Genre</b> checks hit potential, and <b>Personal Stylist</b> matches your taste.
                                    <br/>
                                    Use 👍/👎 in the final column to <b>train your personal model</b>—Agent 3 learns from every choice you make.
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={runJuryFilter}
                            disabled={isRunningJury}
                            style={{ 
                                padding: '12px 24px', 
                                borderRadius: '8px', 
                                border: '2px solid #FF9068', 
                                background: isRunningJury ? '#333' : 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                color: '#fff',
                                cursor: isRunningJury ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                marginLeft: '20px',
                                fontSize: '1rem',
                                boxShadow: isRunningJury ? 'none' : '0 4px 15px rgba(255, 75, 31, 0.3)',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {isRunningJury ? '⏳ Analyzing...' : '⚖️ Run Jury Filter'}
                        </button>
                    </div>

                    <div className="jury-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', height: 'calc(100vh - 250px)', minHeight: '500px' }}>
            {/* Column 1: All Generated (Unprocessed or Failed) */}
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#888', textAlign: 'center', flexShrink: 0 }}>ALL GENERATED</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', overflowX: 'hidden', flex: 1, paddingRight: '5px' }}>
                    {jurySongs.filter(s => !s.is_rejected && !s.pass_a1).map(song => (
                        <div key={song.task_id} style={{ background: '#111', padding: '12px', borderRadius: '8px', border: '1px solid #222', flexShrink: 0 }}>
                            <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={song.title}>{song.title}</div>
                            {song.audio_url && <audio src={song.audio_url} controls style={{ width: '100%', height: '30px', marginTop: '5px' }} />}
                            
                            {/* AI METRICS DISPLAY */}
                            {song.technical_metrics && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.65rem', border: '1px solid #222' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                                        <div style={{ color: '#888' }}>⚡ Energy: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).rms * 100).toFixed(1)}%</span></div>
                                        <div style={{ color: '#888' }}>✨ Clarity: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).clarity / 1000).toFixed(1)}k</span></div>
                                        <div style={{ color: '#888' }}>🥁 BPM: <span style={{ color: '#eee' }}>{Math.round(JSON.parse(song.technical_metrics).bpm)}</span></div>
                                        <div style={{ color: '#888' }}>🎯 Match: <span style={{ color: song.pass_a3 ? '#1DB954' : '#ff4444' }}>{song.pass_a3 ? 'High' : 'Low'}</span></div>
                                    </div>
                                    {!song.pass_a1 && song.jury_processed === 1 && song.technical_metrics && (
                                        <div style={{ color: '#ff4444', borderTop: '1px solid #333', paddingTop: '5px', marginTop: '5px', fontSize: '0.6rem' }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>⚠️ Quality Issues:</div>
                                            {JSON.parse(song.technical_metrics).reasons?.map((reason, idx) => (
                                                <div key={idx}>• {reason}</div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                <button onClick={() => handleJuryAction(song.task_id, 'promote', 1)} style={{ background: '#FF906833', color: '#FF9068', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Force Promote →</button>
                                <button onClick={() => handleJuryAction(song.task_id, 'demote')} style={{ background: 'rgba(255,68,68,0.1)', color: '#ff4444', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Reject</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

                    {/* Column 2: Layer 1 - Sound Engineer */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#888', textAlign: 'center', flexShrink: 0 }}>1. SOUND ENGINEER</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', overflowX: 'hidden', flex: 1, paddingRight: '5px' }}>
                            {jurySongs.filter(s => s.pass_a1 && !s.pass_a2 && !s.is_rejected).map(song => (
                                <div key={song.task_id} style={{ background: '#111', padding: '12px', borderRadius: '8px', border: '1px solid #222', flexShrink: 0 }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={song.title}>{song.title}</div>
                                    {song.audio_url && <audio src={song.audio_url} controls style={{ width: '100%', height: '30px', marginTop: '5px' }} />}
                                    
                                    {/* AI METRICS DISPLAY */}
                                    {song.technical_metrics && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.65rem', border: '1px solid #222' }}>
                                            <div style={{ color: '#888' }}>⚡ Energy: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).rms * 100).toFixed(1)}%</span></div>
                                            <div style={{ color: '#888' }}>✨ Clarity: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).clarity / 1000).toFixed(1)}k</span></div>
                                            <div style={{ color: '#888' }}>🥁 BPM: <span style={{ color: '#eee' }}>{Math.round(JSON.parse(song.technical_metrics).bpm)}</span></div>
                                            <div style={{ color: '#888' }}>🎯 Match: <span style={{ color: song.pass_a3 ? '#1DB954' : '#ff4444' }}>{song.pass_a3 ? 'High' : 'Low'}</span></div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                        <button onClick={() => handleJuryAction(song.task_id, 'promote', 2)} style={{ background: '#FF906833', color: '#FF9068', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Promote →</button>
                                        <button onClick={() => handleJuryAction(song.task_id, 'demote')} style={{ background: 'rgba(255,68,68,0.1)', color: '#ff4444', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Reject</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Column 3: Layer 2 - Market Specialist */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '15px', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#888', textAlign: 'center', flexShrink: 0 }}>2. MARKET GENRE</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', overflowX: 'hidden', flex: 1, paddingRight: '5px' }}>
                            {jurySongs.filter(s => s.pass_a2 && !s.pass_a3 && !s.is_rejected).map(song => (
                                <div key={song.task_id} style={{ background: '#111', padding: '12px', borderRadius: '8px', border: '1px solid #222', flexShrink: 0 }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={song.title}>{song.title}</div>
                                    {song.audio_url && <audio src={song.audio_url} controls style={{ width: '100%', height: '30px', marginTop: '5px' }} />}
                                    
                                    {/* AI METRICS DISPLAY */}
                                    {song.technical_metrics && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.65rem', border: '1px solid #222' }}>
                                            <div style={{ color: '#888' }}>⚡ Energy: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).rms * 100).toFixed(1)}%</span></div>
                                            <div style={{ color: '#888' }}>✨ Clarity: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).clarity / 1000).toFixed(1)}k</span></div>
                                            <div style={{ color: '#888' }}>🥁 BPM: <span style={{ color: '#eee' }}>{Math.round(JSON.parse(song.technical_metrics).bpm)}</span></div>
                                            <div style={{ color: '#888' }}>🎯 Match: <span style={{ color: song.pass_a3 ? '#1DB954' : '#ff4444' }}>{song.pass_a3 ? 'High' : 'Low'}</span></div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                                        <button onClick={() => handleJuryAction(song.task_id, 'promote', 3)} style={{ background: '#FF906833', color: '#FF9068', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Promote →</button>
                                        <button onClick={() => handleJuryAction(song.task_id, 'demote')} style={{ background: 'rgba(255,68,68,0.1)', color: '#ff4444', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '0.7rem', cursor: 'pointer' }}>Reject</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Column 4: Layer 3 - Personal Stylist */}
                    <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '15px', border: '1px solid #FF906833', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexShrink: 0 }}>
                            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#FF9068', textAlign: 'center', flex: 1 }}>3. PERSONAL STYLIST</h3>
                            <div style={{ fontSize: '0.6rem', color: '#666', background: '#222', padding: '2px 6px', borderRadius: '10px' }} title="Number of liked songs training the model">
                                Profile: {jurySongs.filter(s => s.user_rating === 'like').length} Likes
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', overflowX: 'hidden', flex: 1, paddingRight: '5px' }}>
                            {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).map(song => (
                                <div key={song.task_id} style={{ background: '#111', padding: '12px', borderRadius: '8px', border: '1px solid #FF906855', flexShrink: 0 }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={song.title}>{song.title}</div>
                                    {song.audio_url && <audio src={song.audio_url} controls style={{ width: '100%', height: '30px', marginTop: '5px' }} />}
                                    
                                    {/* AI METRICS DISPLAY */}
                                    {song.technical_metrics && (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginTop: '10px', padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '6px', fontSize: '0.65rem', border: '1px solid #222' }}>
                                            <div style={{ color: '#888' }}>⚡ Energy: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).rms * 100).toFixed(1)}%</span></div>
                                            <div style={{ color: '#888' }}>✨ Clarity: <span style={{ color: '#eee' }}>{(JSON.parse(song.technical_metrics).clarity / 1000).toFixed(1)}k</span></div>
                                            <div style={{ color: '#888' }}>🥁 BPM: <span style={{ color: '#eee' }}>{Math.round(JSON.parse(song.technical_metrics).bpm)}</span></div>
                                            <div style={{ color: '#888' }}>🎯 Match: <span style={{ color: song.pass_a3 ? '#1DB954' : '#ff4444' }}>{song.pass_a3 ? 'High' : 'Low'}</span></div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
                                        <button 
                                            onClick={() => handleRateSong(song.task_id, 'like')} 
                                            style={{ 
                                                flex: 1, 
                                                background: song.user_rating === 'like' ? '#1DB954' : '#222', 
                                                border: song.user_rating === 'like' ? '2px solid #1DB954' : 'none',
                                                padding: '6px', 
                                                borderRadius: '4px', 
                                                cursor: 'pointer',
                                                boxShadow: song.user_rating === 'like' ? '0 0 10px rgba(29, 185, 84, 0.5)' : 'none',
                                                transform: song.user_rating === 'like' ? 'scale(1.05)' : 'scale(1)'
                                            }}
                                        >
                                            👍
                                        </button>
                                        <button 
                                            onClick={() => handleRateSong(song.task_id, 'dislike')} 
                                            style={{ 
                                                flex: 1, 
                                                background: song.user_rating === 'dislike' ? '#ff4444' : '#222', 
                                                border: song.user_rating === 'dislike' ? '2px solid #ff4444' : 'none',
                                                padding: '6px', 
                                                borderRadius: '4px', 
                                                cursor: 'pointer',
                                                boxShadow: song.user_rating === 'dislike' ? '0 0 10px rgba(255, 68, 68, 0.5)' : 'none',
                                                transform: song.user_rating === 'dislike' ? 'scale(1.05)' : 'scale(1)'
                                            }}
                                        >
                                            👎
                                        </button>
                                    </div>
                                    <div style={{ fontSize: '0.6rem', color: song.user_rating ? '#1DB954' : '#555', marginTop: '5px', textAlign: 'center', fontWeight: song.user_rating ? 'bold' : 'normal' }}>
                                        {song.user_rating ? '✓ Feedback recorded' : 'Learning from your feedback...'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* YouTube Playlist Tab */}
        {activeSubTab === 'playlist' && (
            <div style={{ marginTop: '20px', paddingBottom: '100px', overflowY: 'auto', maxHeight: 'calc(100vh - 150px)' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '80px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                        <div>
                            <h2 style={{ color: '#FF9068', margin: 0 }}>🎬 YouTube Playlist Creator</h2>
                            <p style={{ color: '#888', margin: '5px 0 0 0' }}>Complete automation from lyrics to YouTube upload</p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {playlistStage !== 'intro' && (
                                <button
                                    onClick={() => {
                                        const stages = ['intro', 'lyrics', 'music', 'jury', 'images', 'video', 'preview', 'metadata', 'final-preview', 'upload'];
                                        const currentIndex = stages.indexOf(playlistStage);
                                        if (currentIndex > 0) {
                                            setPlaylistStage(stages[currentIndex - 1]);
                                        }
                                    }}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '6px',
                                        border: '1px solid #666',
                                        background: '#222',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    ← Back
                                </button>
                            )}
                            <button
                                onClick={async () => {
                                    setModal({
                                        isOpen: true,
                                        type: 'confirm',
                                        title: 'Reset Workflow?',
                                        message: 'Reset workflow and start over? This will stop any ongoing generation and clear your current progress. Your YouTube connection will remain intact.',
                                        showCancel: true,
                                        onConfirm: async () => {
                                            setModal({ ...modal, isOpen: false });
                                            try {
                                                // Create new session to stop ongoing generation
                                                await fetch(`${API_URL}/sessions/new`, { method: 'POST' });
                                            
                                            // Clear image polling interval
                                            if (imagePollInterval) {
                                                clearInterval(imagePollInterval);
                                                setImagePollInterval(null);
                                            }
                                            
                                            // Clear only generation-related state (NOT YouTube auth)
                                            setPlaylistStage('intro');
                                            setPlaylistConfig({ vibe: 'energetic', numSongs: 5, songOrder: [] });
                                            setPlaylistSongs([]);
                                            setImageCandidates({});
                                            setSelectedImages({});
                                            setIsGeneratingImages(false);
                                            setImageGenProgress('');
                                            setPlaylistMetadata({ title: '', description: '', timestamps: [] });
                                            setTaskStatuses({});
                                            setJurySongs([]);
                                            setLogs([]);
                                            setProgress(0);
                                            setStatusMessage('');
                                            setIsGenerating(false);
                                            setIsRunningJury(false);
                                            
                                            // Note: YouTube authentication is stored in the backend database
                                            // and is NOT affected by this reset
                                            } catch (err) {
                                                console.error('Failed to reset:', err);
                                                setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to reset. Please refresh the page.', onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                            }
                                        },
                                        onClose: () => setModal({ ...modal, isOpen: false })
                                    });
                                }}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    border: '1px solid #FF4B1F',
                                    background: 'transparent',
                                    color: '#FF9068',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                🔄 Reset
                            </button>
                        </div>
                    </div>

                    {/* Progress Steps */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '40px', padding: '20px', background: '#1a1a1a', borderRadius: '12px' }}>
                        {[
                            { id: 'intro', label: 'Setup', icon: '🚀' },
                            { id: 'lyrics', label: 'Lyrics', icon: '📝' },
                            { id: 'music', label: 'Music', icon: '🎵' },
                            { id: 'jury', label: 'Filter', icon: '⚖️' },
                            { id: 'images', label: 'Images', icon: '🎨' },
                            { id: 'video', label: 'Videos', icon: '🎥' },
                            { id: 'preview', label: 'Preview', icon: '👁️' },
                            { id: 'metadata', label: 'Metadata', icon: '📋' },
                            { id: 'final-preview', label: 'Final Preview', icon: '🎬' },
                            { id: 'upload', label: 'Upload', icon: '📤' }
                        ].map((step) => (
                            <div key={step.id} style={{ textAlign: 'center' }}>
                                <div style={{ 
                                    width: '45px', 
                                    height: '45px', 
                                    borderRadius: '50%', 
                                    background: playlistStage === step.id ? 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)' : '#333',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 8px',
                                    fontSize: '1.3rem',
                                    border: playlistStage === step.id ? '2px solid #FF4B1F' : 'none'
                                }}>
                                    {step.icon}
                                </div>
                                <div style={{ color: playlistStage === step.id ? '#FF9068' : '#666', fontSize: '0.75rem', fontWeight: playlistStage === step.id ? 'bold' : 'normal' }}>
                                    {step.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Stage Content */}
                    {playlistStage === 'intro' && (
                        <div style={{ background: '#1a1a1a', padding: '40px', borderRadius: '12px', marginBottom: '80px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎬</div>
                                <h3 style={{ color: '#fff', marginBottom: '15px', fontSize: '1.5rem' }}>Complete YouTube Playlist Automation</h3>
                                <p style={{ color: '#888', lineHeight: '1.8', fontSize: '0.95rem' }}>
                                    End-to-end workflow from lyrics generation to YouTube upload
                                </p>
                            </div>

                            <div style={{ maxWidth: '600px', margin: '0 auto 40px' }}>
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Music Vibe</label>
                                    <select 
                                        value={playlistConfig.vibe} 
                                        onChange={(e) => setPlaylistConfig({...playlistConfig, vibe: e.target.value})}
                                        style={{ width: '100%', padding: '12px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '1rem' }}
                                    >
                                        <option value="energetic">⚡ Energetic (Pop/Rock/Dance)</option>
                                        <option value="sad">💧 Sad (Ballad/Melancholic)</option>
                                        <option value="emotional">💙 Emotional (Soul/R&B)</option>
                                        <option value="chill">🎵 Chill (Lo-fi/Jazz)</option>
                                    </select>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Language</label>
                                    <select 
                                        value={playlistConfig.language} 
                                        onChange={(e) => setPlaylistConfig({...playlistConfig, language: e.target.value})}
                                        style={{ width: '100%', padding: '12px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '1rem' }}
                                    >
                                        <option value="Korean">🇰🇷 Korean (한국어)</option>
                                        <option value="English">🇺🇸 English</option>
                                    </select>
                                    <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '5px' }}>Lyrics and title will be strictly in this language only</p>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Singer Gender</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                        <button
                                            onClick={() => setPlaylistConfig({...playlistConfig, gender: null})}
                                            style={{
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: playlistConfig.gender === null ? '2px solid #FF9068' : '1px solid #444',
                                                background: playlistConfig.gender === null ? 'rgba(255, 144, 104, 0.1)' : '#111',
                                                color: playlistConfig.gender === null ? '#FF9068' : '#888',
                                                cursor: 'pointer',
                                                fontWeight: playlistConfig.gender === null ? 'bold' : 'normal',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            🎭 Auto
                                        </button>
                                        <button
                                            onClick={() => setPlaylistConfig({...playlistConfig, gender: 'male'})}
                                            style={{
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: playlistConfig.gender === 'male' ? '2px solid #FF9068' : '1px solid #444',
                                                background: playlistConfig.gender === 'male' ? 'rgba(255, 144, 104, 0.1)' : '#111',
                                                color: playlistConfig.gender === 'male' ? '#FF9068' : '#888',
                                                cursor: 'pointer',
                                                fontWeight: playlistConfig.gender === 'male' ? 'bold' : 'normal',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            👨 Male
                                        </button>
                                        <button
                                            onClick={() => setPlaylistConfig({...playlistConfig, gender: 'female'})}
                                            style={{
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: playlistConfig.gender === 'female' ? '2px solid #FF9068' : '1px solid #444',
                                                background: playlistConfig.gender === 'female' ? 'rgba(255, 144, 104, 0.1)' : '#111',
                                                color: playlistConfig.gender === 'female' ? '#FF9068' : '#888',
                                                cursor: 'pointer',
                                                fontWeight: playlistConfig.gender === 'female' ? 'bold' : 'normal',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            👩 Female
                                        </button>
                                    </div>
                                    <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '5px' }}>Select vocal gender for music generation</p>
                                </div>

                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Number of Songs</label>
                                    <input 
                                        type="number" 
                                        min="3" 
                                        max="10" 
                                        value={playlistConfig.numSongs}
                                        onChange={(e) => setPlaylistConfig({...playlistConfig, numSongs: parseInt(e.target.value)})}
                                        style={{ width: '100%', padding: '12px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '1rem' }}
                                    />
                                    <p style={{ color: '#666', fontSize: '0.8rem', marginTop: '5px' }}>We'll generate this many songs and filter the best ones</p>
                                </div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <button
                                    onClick={() => setPlaylistStage('lyrics')}
                                    style={{
                                        padding: '18px 50px',
                                        fontSize: '1.2rem',
                                        borderRadius: '10px',
                                        border: 'none',
                                        background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        boxShadow: '0 6px 20px rgba(255, 75, 31, 0.4)',
                                        transition: 'transform 0.2s, box-shadow 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 25px rgba(255, 75, 31, 0.5)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 6px 20px rgba(255, 75, 31, 0.4)';
                                    }}
                                >
                                    🚀 Start Playlist Creation
                                </button>
                            </div>
                        </div>
                    )}

                    {playlistStage === 'lyrics' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>📝 Stage 1: Generate Lyrics</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Creating {playlistConfig.numSongs} unique song lyrics with {playlistConfig.vibe} vibe</p>
                            
                            {!isGenerating && Object.keys(taskStatuses).length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✍️</div>
                                    <button
                                        onClick={async () => {
                                            setIsGenerating(true);
                                            setProgress(5);
                                            setLogs([]);
                                            setTaskStatuses({});
                                            setStatusMessage(`Generating ${playlistConfig.numSongs} songs in ${playlistConfig.language} with ${playlistConfig.gender || 'auto'} vocals...`);

                                            try {
                                                const response = await fetch(`${API_URL}/gen-pro`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({
                                                        vibe: playlistConfig.vibe,
                                                        count: parseInt(playlistConfig.numSongs),
                                                        folder_id: null,
                                                        language: playlistConfig.language,
                                                        gender: playlistConfig.gender
                                                    })
                                                });

                                                const reader = response.body.getReader();
                                                const decoder = new TextDecoder();

                                                while (true) {
                                                    const { value, done } = await reader.read();
                                                    if (done) break;

                                                    const chunk = decoder.decode(value);
                                                    const lines = chunk.split('\n').filter(l => l.trim());

                                                    for (const line of lines) {
                                                        try {
                                                            const update = JSON.parse(line);
                                                            setProgress(update.percent || progress);
                                                            setStatusMessage(update.message);
                                                            setLogs(prev => [update.message, ...prev.slice(0, 19)]);

                                                            if (update.status === 'complete') {
                                                                setIsGenerating(false);
                                                                // Gen-pro returns task_ids and tasks data
                                                                if (update.data && update.data.task_ids) {
                                                                    console.log('📦 Received task IDs:', update.data.task_ids);
                                                                    const initialStatuses = {};
                                                                    update.data.task_ids.forEach(id => {
                                                                        initialStatuses[id] = { id, status: 'submitted', drive_status: 'pending' };
                                                                    });
                                                                    setTaskStatuses(initialStatuses);
                                                                    // Move to music stage automatically since gen-pro generates both lyrics and music
                                                                    setPlaylistStage('music');
                                                                }
                                                            }
                                                        } catch (e) {
                                                            console.error('Parse error:', e);
                                                        }
                                                    }
                                                }
                                            } catch (err) {
                                                console.error('Lyrics generation error:', err);
                                                setIsGenerating(false);
                                                setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to generate lyrics: ' + err.message, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                            }
                                        }}
                                        style={{
                                            padding: '15px 40px',
                                            fontSize: '1.1rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                        }}
                                    >
                                        📝 Generate Lyrics
                                    </button>
                                </div>
                            )}

                            {(isGenerating || Object.keys(taskStatuses).length > 0) && (
                                <div>
                                    <div style={{ marginBottom: '20px', padding: '15px', background: '#111', borderRadius: '8px' }}>
                                        <div style={{ color: '#FF9068', fontWeight: 'bold', marginBottom: '10px' }}>⏳ Generation in Progress...</div>
                                        <div style={{ color: '#888', fontSize: '0.9rem' }}>{statusMessage}</div>
                                    </div>

                                    {Object.keys(taskStatuses).length > 0 && (
                                        <div style={{ display: 'grid', gap: '10px' }}>
                                            {Object.entries(taskStatuses).map(([taskId, task]) => (
                                                <div key={taskId} style={{ background: '#111', padding: '15px', borderRadius: '8px', border: '1px solid #222' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#eee' }}>{task.title || task.subject || taskId}</div>
                                                        <div style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '4px', background: task.status === 'lyrics_complete' ? 'rgba(29, 185, 84, 0.1)' : 'rgba(255, 144, 104, 0.1)', color: task.status === 'lyrics_complete' ? '#1DB954' : '#FF9068', border: `1px solid ${task.status === 'lyrics_complete' ? '#1DB95433' : '#FF906833'}` }}>
                                                            {task.status === 'lyrics_complete' ? 'COMPLETE' : task.status.toUpperCase()}
                                                        </div>
                                                    </div>
                                                    {task.lyrics && (
                                                        <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '10px', maxHeight: '100px', overflow: 'hidden' }}>
                                                            {task.lyrics.substring(0, 150)}...
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!isGenerating && Object.keys(taskStatuses).length > 0 && Object.values(taskStatuses).every(t => t.status === 'lyrics_complete') && (
                                        <div style={{ marginTop: '30px', textAlign: 'center' }}>
                                            <button
                                                onClick={async () => {
                                                    // Trigger music generation for existing lyrics
                                                    setPlaylistStage('music');
                                                    setIsGenerating(true);
                                                    setStatusMessage('Starting music generation for existing lyrics...');
                                                    
                                                    console.log('🎵 Using existing lyrics:', playlistSongs);
                                                    
                                                    try {
                                                        const response = await fetch(`${API_URL}/generate-music-only`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({
                                                                songs: playlistSongs,
                                                                folder_id: null
                                                            })
                                                        });

                                                        const reader = response.body.getReader();
                                                        const decoder = new TextDecoder();

                                                        while (true) {
                                                            const { value, done } = await reader.read();
                                                            if (done) break;

                                                            const chunk = decoder.decode(value);
                                                            const lines = chunk.split('\n').filter(l => l.trim());

                                                            for (const line of lines) {
                                                                try {
                                                                    const update = JSON.parse(line);
                                                                    console.log('🎵 Music gen update:', update);
                                                                    setProgress(update.percent || 0);
                                                                    setStatusMessage(update.message || '');
                                                                    setLogs(prev => [update.message, ...prev.slice(0, 19)]);

                                                                    if (update.status === 'complete' || update.status === 'submitted') {
                                                                        setIsGenerating(false);
                                                                        console.log('✅ Music generation complete, data:', update.data);
                                                                        
                                                                        // Update task statuses with real task IDs from backend
                                                                        if (update.data && update.data.tasks) {
                                                                            console.log('📋 Setting task statuses:', update.data.tasks);
                                                                            const newStatuses = {};
                                                                            update.data.tasks.forEach(task => {
                                                                                newStatuses[task.id] = {
                                                                                    id: task.id,
                                                                                    title: task.title,
                                                                                    lyrics: task.lyrics,
                                                                                    status: task.status || 'submitted',
                                                                                    drive_status: 'pending',
                                                                                    audio_url: null
                                                                                };
                                                                            });
                                                                            setTaskStatuses(newStatuses);
                                                                            console.log('✅ Task statuses set, polling should start');
                                                                        } else {
                                                                            console.warn('⚠️ No tasks in response data');
                                                                        }
                                                                    }
                                                                } catch (e) {
                                                                    console.error('Parse error:', e);
                                                                }
                                                            }
                                                        }
                                                    } catch (err) {
                                                        console.error('Music generation error:', err);
                                                        setIsGenerating(false);
                                                        setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to start music generation: ' + err.message, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                    }
                                                }}
                                                style={{
                                                    padding: '15px 40px',
                                                    fontSize: '1.1rem',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    background: 'linear-gradient(90deg, #1DB954 0%, #1ed760 100%)',
                                                    color: '#fff',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 4px 15px rgba(29, 185, 84, 0.3)'
                                                }}
                                            >
                                                ✓ Lyrics Complete - Continue to Music
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {playlistStage === 'music' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>🎵 Stage 2: Generate Music</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Creating audio for all songs with lyrics</p>
                            
                            {Object.keys(taskStatuses).length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '20px', color: '#666' }}>⚠️</div>
                                    <p style={{ color: '#888' }}>No songs with lyrics found. Please generate lyrics first.</p>
                                    <button
                                        onClick={() => setPlaylistStage('lyrics')}
                                        style={{
                                            padding: '12px 30px',
                                            fontSize: '1rem',
                                            borderRadius: '8px',
                                            border: '1px solid #666',
                                            background: '#222',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            marginTop: '20px'
                                        }}
                                    >
                                        ← Back to Lyrics
                                    </button>
                                </div>
                            )}

                            {Object.keys(taskStatuses).length > 0 && (
                                <div>
                                    {Object.values(taskStatuses).every(t => t.audio_url && t.drive_status === 'uploaded') ? (
                                        <div style={{ textAlign: 'center', padding: '40px' }}>
                                            <div style={{ fontSize: '3rem', marginBottom: '20px' }}>✅</div>
                                            <p style={{ color: '#1DB954', fontWeight: 'bold', marginBottom: '30px' }}>All songs have been generated and uploaded!</p>
                                            <button
                                                onClick={() => setPlaylistStage('jury')}
                                                style={{
                                                    padding: '15px 40px',
                                                    fontSize: '1.1rem',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    background: 'linear-gradient(90deg, #1DB954 0%, #1ed760 100%)',
                                                    color: '#fff',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 4px 15px rgba(29, 185, 84, 0.3)'
                                                }}
                                            >
                                                ✓ Ready - Continue to Filter
                                            </button>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{ marginBottom: '20px', padding: '15px', background: '#111', borderRadius: '8px' }}>
                                                <div style={{ color: '#FF9068', fontWeight: 'bold', marginBottom: '10px' }}>
                                                    🎵 Music Generation Status
                                                </div>
                                                <div style={{ color: '#888', fontSize: '0.9rem' }}>
                                                    {Object.values(taskStatuses).filter(t => t.audio_url).length} / {Object.keys(taskStatuses).length} songs have music
                                                </div>
                                            </div>

                                            <div style={{ display: 'grid', gap: '10px' }}>
                                                {Object.entries(taskStatuses).map(([taskId, task]) => {
                                                    // Determine detailed status message
                                                    let statusText = 'PENDING';
                                                    let statusColor = '#FF9068';
                                                    let statusBg = 'rgba(255, 144, 104, 0.1)';
                                                    let statusBorder = '#FF906833';
                                                    let statusIcon = '⏳';
                                                    
                                                    if (task.drive_status === 'uploaded') {
                                                        statusText = 'UPLOADED';
                                                        statusColor = '#1DB954';
                                                        statusBg = 'rgba(29, 185, 84, 0.1)';
                                                        statusBorder = '#1DB95433';
                                                        statusIcon = '✅';
                                                    } else if (task.drive_status === 'uploading') {
                                                        statusText = 'UPLOADING';
                                                        statusColor = '#FFB800';
                                                        statusBg = 'rgba(255, 184, 0, 0.1)';
                                                        statusBorder = '#FFB80033';
                                                        statusIcon = '☁️';
                                                    } else if (task.audio_url) {
                                                        statusText = 'PROCESSING';
                                                        statusColor = '#FFB800';
                                                        statusBg = 'rgba(255, 184, 0, 0.1)';
                                                        statusBorder = '#FFB80033';
                                                        statusIcon = '⚙️';
                                                    } else if (task.status === 'streaming' || task.status === 'submitted') {
                                                        statusText = 'GENERATING';
                                                        statusColor = '#FF9068';
                                                        statusBg = 'rgba(255, 144, 104, 0.1)';
                                                        statusBorder = '#FF906833';
                                                        statusIcon = '🎵';
                                                    }
                                                    
                                                    return (
                                                        <div key={taskId} style={{ background: '#111', padding: '15px', borderRadius: '8px', border: '1px solid #222' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                                <div style={{ fontWeight: 'bold', color: '#eee' }}>{task.title || taskId}</div>
                                                                <div style={{ fontSize: '0.7rem', padding: '3px 8px', borderRadius: '4px', background: statusBg, color: statusColor, border: `1px solid ${statusBorder}` }}>
                                                                    {statusIcon} {statusText}
                                                                </div>
                                                            </div>
                                                            {task.audio_url && (
                                                                <audio src={task.audio_url} controls style={{ width: '100%', height: '30px', marginTop: '10px' }} />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div style={{ marginTop: '30px', textAlign: 'center', padding: '20px', background: '#111', borderRadius: '8px' }}>
                                                <p style={{ color: '#888', fontSize: '0.9rem' }}>
                                                    🎵 Music generation happens automatically in the background.<br/>
                                                    Songs will update to "HAS MUSIC" when complete. This may take several minutes.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {playlistStage === 'jury' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>⚖️ Stage 3: Filter Best Songs</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Run jury filter to select only high-quality songs for your playlist</p>
                            
                            {!isRunningJury && jurySongs.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎯</div>
                                    <button
                                        onClick={async () => {
                                            setIsRunningJury(true);
                                            try {
                                                const res = await fetch(`${API_URL}/jury/run-filter`, { method: 'POST' });
                                                const data = await res.json();
                                                if (res.ok) {
                                                    setModal({ isOpen: true, type: 'success', title: 'Analysis Complete', message: `Jury analysis complete! Processed ${data.processed} songs.`, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                    await fetchJurySongs();
                                                } else {
                                                    setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Error running jury filter: ' + (data.error || 'Unknown error'), onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                }
                                            } catch (err) {
                                                setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Error: ' + err.message, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                            } finally {
                                                setIsRunningJury(false);
                                            }
                                        }}
                                        disabled={isRunningJury}
                                        style={{
                                            padding: '15px 40px',
                                            fontSize: '1.1rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: isRunningJury ? '#333' : 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                            color: '#fff',
                                            cursor: isRunningJury ? 'not-allowed' : 'pointer',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)',
                                            opacity: isRunningJury ? 0.6 : 1
                                        }}
                                    >
                                        {isRunningJury ? '⏳ Analyzing...' : '⚖️ Run Jury Filter'}
                                    </button>
                                </div>
                            )}

                            {isRunningJury && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
                                    <p style={{ color: '#FF9068', fontWeight: 'bold' }}>Analyzing songs with AI jury...</p>
                                    <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '10px' }}>This may take a minute</p>
                                </div>
                            )}

                            {!isRunningJury && jurySongs.length > 0 && (
                                <div>
                                    <div style={{ marginBottom: '20px', padding: '15px', background: '#111', borderRadius: '8px' }}>
                                        <div style={{ color: '#1DB954', fontWeight: 'bold', marginBottom: '10px' }}>
                                            ✓ Jury Filter Complete
                                        </div>
                                        <div style={{ color: '#888', fontSize: '0.9rem' }}>
                                            {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).length} songs passed all filters
                                        </div>
                                    </div>

                                    <div style={{ marginBottom: '30px', padding: '20px', background: '#111', borderRadius: '8px' }}>
                                        <h4 style={{ color: '#FF9068', marginTop: 0, marginBottom: '15px' }}>Filtered Songs:</h4>
                                        {playlistSongs.length > 0 ? (
                                            <div style={{ marginBottom: '30px' }}>
                                                <div style={{ background: '#0a0a0a', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                                                    <h4 style={{ color: '#1DB954', marginBottom: '15px' }}>✓ Selected Songs ({playlistSongs.length})</h4>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {playlistSongs.map((song, index) => (
                                                            <div key={song.task_id} style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '15px',
                                                                padding: '12px',
                                                                background: '#1a1a1a',
                                                                borderRadius: '6px',
                                                                border: '1px solid #333'
                                                            }}>
                                                                <div style={{ color: '#666', fontWeight: 'bold', minWidth: '30px' }}>{index + 1}.</div>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ color: '#eee', fontWeight: 'bold' }}>{song.title}</div>
                                                                    <div style={{ color: '#666', fontSize: '0.85rem' }}>{song.vibe}</div>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <button
                                                                        onClick={() => {
                                                                            if (index > 0) {
                                                                                const newList = [...playlistSongs];
                                                                                [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
                                                                                setPlaylistSongs(newList);
                                                                            }
                                                                        }}
                                                                        disabled={index === 0}
                                                                        style={{
                                                                            padding: '6px 12px',
                                                                            background: index === 0 ? '#222' : '#333',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            color: index === 0 ? '#555' : '#eee',
                                                                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                                                                            fontSize: '0.85rem'
                                                                        }}
                                                                    >
                                                                        ▲
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            if (index < playlistSongs.length - 1) {
                                                                                const newList = [...playlistSongs];
                                                                                [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
                                                                                setPlaylistSongs(newList);
                                                                            }
                                                                        }}
                                                                        disabled={index === playlistSongs.length - 1}
                                                                        style={{
                                                                            padding: '6px 12px',
                                                                            background: index === playlistSongs.length - 1 ? '#222' : '#333',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            color: index === playlistSongs.length - 1 ? '#555' : '#eee',
                                                                            cursor: index === playlistSongs.length - 1 ? 'not-allowed' : 'pointer',
                                                                            fontSize: '0.85rem'
                                                                        }}
                                                                    >
                                                                        ▼
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            setPlaylistSongs(playlistSongs.filter(s => s.task_id !== song.task_id));
                                                                        }}
                                                                        style={{
                                                                            padding: '6px 12px',
                                                                            background: '#8B0000',
                                                                            border: 'none',
                                                                            borderRadius: '4px',
                                                                            color: '#fff',
                                                                            cursor: 'pointer',
                                                                            fontSize: '0.85rem'
                                                                        }}
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setPlaylistStage('images')}
                                                    style={{
                                                        padding: '15px 40px',
                                                        fontSize: '1.1rem',
                                                        borderRadius: '8px',
                                                        border: 'none',
                                                        background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                                        color: '#fff',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold',
                                                        boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)',
                                                        width: '100%'
                                                    }}
                                                >
                                                    🎨 Continue to Playlist Cover Generation
                                                </button>
                                            </div>
                                        ) : null}
                                        
                                        {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).length === 0 ? (
                                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                                <div style={{ fontSize: '3rem', marginBottom: '20px', color: '#666' }}>😔</div>
                                                <p style={{ color: '#888', fontSize: '1.1rem', marginBottom: '15px' }}>No songs passed the quality filter</p>
                                                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '30px' }}>
                                                    All generated songs were filtered out based on quality criteria.<br/>
                                                    Try generating more songs or adjusting your configuration.
                                                </p>
                                                <button
                                                    onClick={() => setPlaylistStage('lyrics')}
                                                    style={{
                                                        padding: '12px 30px',
                                                        fontSize: '1rem',
                                                        borderRadius: '8px',
                                                        border: '1px solid #666',
                                                        background: '#222',
                                                        color: '#fff',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    ← Back to Generate More Lyrics
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ display: 'grid', gap: '10px', marginBottom: '30px' }}>
                                                    {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).map(song => (
                                                        <div key={song.task_id} style={{ background: '#0a0a0a', padding: '12px', borderRadius: '6px', border: '1px solid #222' }}>
                                                            <div style={{ fontWeight: 'bold', color: '#eee', marginBottom: '8px' }}>{song.title}</div>
                                                            {song.audio_url && <audio src={song.audio_url} controls style={{ width: '100%', height: '30px' }} />}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ marginTop: '20px' }}>
                                                        <h4 style={{ color: '#FF9068', marginBottom: '15px' }}>📋 Select Songs for Playlist</h4>
                                                        <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '15px' }}>
                                                            Choose which songs to include in your playlist and arrange them in order.
                                                        </p>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                            {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).map(song => {
                                                                const isSelected = playlistSongs.some(s => s.task_id === song.task_id);
                                                                return (
                                                                    <div key={song.task_id} style={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '15px',
                                                                        padding: '12px',
                                                                        background: isSelected ? '#1a3a1a' : '#1a1a1a',
                                                                        borderRadius: '6px',
                                                                        border: isSelected ? '2px solid #1DB954' : '1px solid #333'
                                                                    }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setPlaylistSongs([...playlistSongs, song]);
                                                                                } else {
                                                                                    setPlaylistSongs(playlistSongs.filter(s => s.task_id !== song.task_id));
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                width: '20px',
                                                                                height: '20px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                        />
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ color: '#eee', fontWeight: 'bold' }}>{song.title}</div>
                                                                            <div style={{ color: '#666', fontSize: '0.85rem' }}>{song.vibe}</div>
                                                                        </div>
                                                                        {isSelected && (
                                                                            <div style={{
                                                                                padding: '4px 12px',
                                                                                background: '#1DB954',
                                                                                borderRadius: '12px',
                                                                                color: '#fff',
                                                                                fontSize: '0.8rem',
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                #{playlistSongs.findIndex(s => s.task_id === song.task_id) + 1}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {playlistStage === 'images' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>🎨 Stage 4: Select Playlist Cover</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>AI will generate 4 playlist cover candidates with your playlist title and song list. Choose your favorite.</p>
                            
                            {!isGeneratingImages && !imageCandidates.candidates && (
                                <div>
                                    <div style={{ marginBottom: '30px', padding: '20px', background: '#111', borderRadius: '8px' }}>
                                        <label style={{ display: 'block', color: '#FF9068', marginBottom: '10px', fontWeight: 'bold' }}>
                                            📝 Playlist Cover Template
                                        </label>
                                        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '10px' }}>
                                            Use <code style={{ background: '#222', padding: '2px 6px', borderRadius: '3px' }}>{'{playlist_title}'}</code> and <code style={{ background: '#222', padding: '2px 6px', borderRadius: '3px' }}>{'{song_list}'}</code> as placeholders
                                        </p>
                                        <textarea
                                            value={playlistCoverPrompt}
                                            onChange={(e) => setPlaylistCoverPrompt(e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '200px',
                                                padding: '15px',
                                                background: '#0a0a0a',
                                                border: '1px solid #333',
                                                borderRadius: '6px',
                                                color: '#eee',
                                                fontSize: '0.9rem',
                                                fontFamily: 'monospace',
                                                resize: 'vertical'
                                            }}
                                        />
                                    </div>
                                    
                                    <div style={{ textAlign: 'center', padding: '40px' }}>
                                        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🎨</div>
                                        <p style={{ color: '#888', marginBottom: '30px' }}>Ready to generate playlist cover for {jurySongs.filter(s => s.pass_a3 && !s.is_rejected).length} song(s)</p>
                                        <button
                                            onClick={async () => {
                                                setIsGeneratingImages(true);
                                                setImageGenProgress('Starting image generation...');
                                                try {
                                                    // Send only song titles in order
                                                    const songTitles = playlistSongs.map(s => s.title);
                                                    const response = await fetch(`${API_URL}/pipeline/generate-images`, {
                                                        method: 'POST',
                                                        headers: { 'Content-Type': 'application/json' },
                                                        body: JSON.stringify({ 
                                                            songs: songTitles,
                                                            prompt: playlistCoverPrompt 
                                                        })
                                                    });
                                                
                                                if (response.ok) {
                                                    setImageGenProgress('Playlist cover generation started! This may take a few minutes...');
                                                    
                                                    // Clear any existing polling interval
                                                    if (imagePollInterval) {
                                                        clearInterval(imagePollInterval);
                                                    }
                                                    
                                                    // Poll for playlist cover candidates
                                                    const pollCandidates = setInterval(async () => {
                                                        const res = await fetch(`${API_URL}/pipeline/image-candidates`);
                                                        if (res.ok) {
                                                            const data = await res.json();
                                                            
                                                            // Check if candidates are ready
                                                            if (data.candidates && data.candidates.length > 0) {
                                                                setImageCandidates(data);
                                                                clearInterval(pollCandidates);
                                                                setImagePollInterval(null);
                                                                setIsGeneratingImages(false);
                                                                setImageGenProgress('');
                                                            }
                                                        }
                                                    }, 3000);
                                                    
                                                    setImagePollInterval(pollCandidates);
                                                    
                                                    // Stop polling after 5 minutes
                                                    setTimeout(() => {
                                                        clearInterval(pollCandidates);
                                                        setImagePollInterval(null);
                                                        setIsGeneratingImages(false);
                                                        setImageGenProgress('Timeout: Image generation took too long');
                                                    }, 300000);
                                                } else {
                                                    setImageGenProgress('Error starting image generation');
                                                    setIsGeneratingImages(false);
                                                }
                                            } catch (error) {
                                                console.error('Image generation error:', error);
                                                setImageGenProgress('Error: ' + error.message);
                                                setIsGeneratingImages(false);
                                            }
                                        }}
                                        style={{
                                            padding: '15px 40px',
                                            fontSize: '1.1rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                        }}
                                    >
                                        🎨 Generate Images
                                    </button>
                                </div>
                                </div>
                            )}
                            
                            {isGeneratingImages && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '20px' }}>⏳</div>
                                    <p style={{ color: '#FF9068', marginBottom: '15px', fontWeight: 'bold' }}>{imageGenProgress}</p>
                                    <p style={{ color: '#888', fontSize: '0.95rem', marginBottom: '10px' }}>
                                        Generating 4 playlist cover candidates using <strong>Gemini Imagen 4</strong>
                                    </p>
                                    <p style={{ color: '#666', fontSize: '0.85rem' }}>
                                        Each cover takes ~30 seconds. This process may take 2-3 minutes total.
                                    </p>
                                </div>
                            )}
                            
                            {!isGeneratingImages && imageCandidates.candidates && imageCandidates.candidates.length > 0 && (
                                <div>
                                    <h4 style={{ color: '#eee', marginBottom: '20px', textAlign: 'center' }}>
                                        {imageCandidates.playlist_title || '트로트 플레이리스트'}
                                    </h4>
                                    
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '30px' }}>
                                        {imageCandidates.candidates.map((candidate, idx) => (
                                            <div
                                                key={idx}
                                                onClick={() => setSelectedImages({ playlistCover: idx })}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: selectedImages.playlistCover === idx ? '4px solid #1DB954' : '2px solid #333',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    transition: 'all 0.2s',
                                                    boxShadow: selectedImages.playlistCover === idx ? '0 8px 25px rgba(29, 185, 84, 0.4)' : 'none'
                                                }}
                                            >
                                                <img 
                                                    src={candidate.image_url} 
                                                    alt={`Cover ${idx + 1}`}
                                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                                />
                                                {selectedImages.playlistCover === idx && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '15px',
                                                        right: '15px',
                                                        background: '#1DB954',
                                                        color: '#fff',
                                                        padding: '8px 15px',
                                                        borderRadius: '6px',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 'bold',
                                                        boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
                                                    }}>
                                                        ✓ SELECTED
                                                    </div>
                                                )}
                                                <div style={{
                                                    padding: '12px',
                                                    background: '#0a0a0a',
                                                    color: '#888',
                                                    fontSize: '0.85rem',
                                                    textAlign: 'center'
                                                }}>
                                                    Option {idx + 1}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {selectedImages.playlistCover !== undefined && (
                                        <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        setModal({ isOpen: true, type: 'loading', title: 'Finalizing Cover', message: 'Burning text into selected cover image...', onConfirm: null });
                                                        
                                                        // Save playlist cover selection and burn text
                                                        const selectRes = await fetch(`${API_URL}/pipeline/select-playlist-cover`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({ 
                                                                selected_index: selectedImages.playlistCover,
                                                                songs: playlistSongs.map(s => s.title)
                                                            })
                                                        });
                                                        
                                                        const selectData = await selectRes.json();
                                                        
                                                        if (selectData.error) {
                                                            setModal({ 
                                                                isOpen: true, 
                                                                type: 'error', 
                                                                title: 'Error', 
                                                                message: selectData.error,
                                                                onConfirm: () => setModal({ ...modal, isOpen: false })
                                                            });
                                                            return;
                                                        }
                                                        
                                                        setModal({ ...modal, isOpen: false });
                                                        setPlaylistStage('video');
                                                        
                                                        // Trigger video creation with song order
                                                        const videoRes = await fetch(`${API_URL}/pipeline/create-playlist-video`, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({
                                                                songs: playlistSongs.map(s => s.title)
                                                            })
                                                        });
                                                        
                                                        const videoData = await videoRes.json();
                                                        
                                                        if (videoData.error) {
                                                            setModal({ 
                                                                isOpen: true, 
                                                                type: 'error', 
                                                                title: 'Video Creation Failed', 
                                                                message: videoData.error,
                                                                onConfirm: () => setModal({ ...modal, isOpen: false })
                                                            });
                                                            return;
                                                        }
                                                        
                                                        // Store session ID for video preview
                                                        setSessionId(videoData.session_id);
                                                        
                                                        // Poll for video creation completion
                                                        let attempts = 0;
                                                        const maxAttempts = 60; // 5 minutes max
                                                        
                                                        const checkVideoStatus = setInterval(async () => {
                                                            attempts++;
                                                            
                                                            if (attempts > maxAttempts) {
                                                                clearInterval(checkVideoStatus);
                                                                setModal({ 
                                                                    isOpen: true, 
                                                                    type: 'error', 
                                                                    title: 'Timeout', 
                                                                    message: 'Video creation is taking longer than expected. Please check back later.',
                                                                    onConfirm: () => setModal({ ...modal, isOpen: false })
                                                                });
                                                                return;
                                                            }
                                                            
                                                            // Check if video is ready by fetching session status
                                                            const statusRes = await fetch(`${API_URL}/pipeline/preview`);
                                                            const statusData = await statusRes.json();
                                                            
                                                            if (statusData.session && statusData.session.playlist_video_path) {
                                                                clearInterval(checkVideoStatus);
                                                                setModal({ ...modal, isOpen: false });
                                                                setPlaylistStage('metadata');
                                                            } else {
                                                                setModal({ 
                                                                    isOpen: true, 
                                                                    type: 'loading', 
                                                                    title: 'Creating Playlist Video', 
                                                                    message: `Creating videos... (${attempts * 5}s)`,
                                                                    onConfirm: null 
                                                                });
                                                            }
                                                        }, 5000); // Check every 5 seconds
                                                        
                                                    } catch (error) {
                                                        console.error('Video creation error:', error);
                                                        setModal({ 
                                                            isOpen: true, 
                                                            type: 'error', 
                                                            title: 'Error', 
                                                            message: 'Failed to create playlist video: ' + error.message,
                                                            onConfirm: () => setModal({ ...modal, isOpen: false })
                                                        });
                                                    }
                                                }}
                                                style={{
                                                    padding: '15px 40px',
                                                    fontSize: '1.1rem',
                                                    borderRadius: '8px',
                                                    border: 'none',
                                                    background: 'linear-gradient(90deg, #1DB954 0%, #1ed760 100%)',
                                                    color: '#fff',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 4px 15px rgba(29, 185, 84, 0.3)'
                                                }}
                                            >
                                                ✓ Confirm Cover & Continue
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {playlistStage === 'video' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>🎥 Stage 5: Create Videos & Reorder</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Arrange songs in your preferred order, then create MP4 videos</p>
                            <div style={{ padding: '20px', background: '#111', borderRadius: '8px', marginBottom: '30px' }}>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '15px' }}>Drag to reorder songs:</p>
                                <div style={{ color: '#666', textAlign: 'center', padding: '20px' }}>Song reordering UI will be implemented here</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <button
                                    onClick={async () => {
                                        setModal({ isOpen: true, type: 'loading', title: 'Creating Videos', message: 'Please wait...', onConfirm: null });
                                        // TODO: Call backend API
                                        setTimeout(() => {
                                            setModal({ ...modal, isOpen: false });
                                            setPlaylistStage('preview');
                                        }, 1000);
                                    }}
                                    style={{
                                        padding: '15px 40px',
                                        fontSize: '1.1rem',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                    }}
                                >
                                    🎥 Create Videos
                                </button>
                            </div>
                        </div>
                    )}

                    {playlistStage === 'preview' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>👁️ Stage 6: Preview Playlist</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Review the final concatenated playlist video before upload</p>
                            <div style={{ background: '#111', padding: '40px', borderRadius: '8px', marginBottom: '30px', textAlign: 'center' }}>
                                <div style={{ fontSize: '3rem', marginBottom: '15px' }}>📹</div>
                                <p style={{ color: '#666' }}>Video preview player will appear here</p>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <button
                                    onClick={() => setPlaylistStage('metadata')}
                                    style={{
                                        padding: '15px 40px',
                                        fontSize: '1.1rem',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                    }}
                                >
                                    ✓ Looks Good, Continue
                                </button>
                            </div>
                        </div>
                    )}

                    {playlistStage === 'metadata' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>📋 Stage 7: Generate Metadata</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>AI-generated YouTube title, description, and timestamps</p>
                            
                            {!playlistMetadata.title && (
                                <div style={{ textAlign: 'center', padding: '40px' }}>
                                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🤖</div>
                                    <button
                                        onClick={async () => {
                                            try {
                                                setModal({ isOpen: true, type: 'loading', title: 'Generating Metadata', message: 'Creating YouTube title, description, and timestamps...', onConfirm: null });
                                                
                                                const res = await fetch(`${API_URL}/pipeline/generate-metadata`, {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ vibe: playlistConfig.vibe })
                                                });
                                                
                                                const data = await res.json();
                                                
                                                if (data.error) {
                                                    setModal({ isOpen: true, type: 'error', title: 'Error', message: data.error, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                    return;
                                                }
                                                
                                                setPlaylistMetadata({
                                                    title: data.title || '',
                                                    description: data.description || '',
                                                    timestamps: data.timestamps || []
                                                });
                                                
                                                setModal({ ...modal, isOpen: false });
                                            } catch (error) {
                                                console.error('Metadata generation error:', error);
                                                setModal({ isOpen: true, type: 'error', title: 'Error', message: 'Failed to generate metadata: ' + error.message, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                            }
                                        }}
                                        style={{
                                            padding: '15px 40px',
                                            fontSize: '1.1rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                        }}
                                    >
                                        🤖 Generate YouTube Metadata
                                    </button>
                                </div>
                            )}
                            
                            {playlistMetadata.title && (
                                <div>
                                    <div style={{ marginBottom: '25px' }}>
                                        <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Playlist Title</label>
                                        <input 
                                            type="text" 
                                            value={playlistMetadata.title}
                                            onChange={(e) => setPlaylistMetadata({...playlistMetadata, title: e.target.value})}
                                            placeholder="Auto-generated title will appear here"
                                            style={{ width: '100%', padding: '12px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '1rem' }}
                                        />
                                    </div>
                                    <div style={{ marginBottom: '25px' }}>
                                        <label style={{ display: 'block', color: '#aaa', marginBottom: '10px', fontSize: '0.9rem', fontWeight: '600' }}>Description with Timestamps</label>
                                        <textarea 
                                            value={playlistMetadata.description}
                                            onChange={(e) => setPlaylistMetadata({...playlistMetadata, description: e.target.value})}
                                            placeholder="Auto-generated description with song timestamps"
                                            style={{ width: '100%', minHeight: '150px', padding: '12px', background: '#111', border: '1px solid #444', color: '#fff', borderRadius: '8px', fontSize: '0.9rem', fontFamily: 'monospace' }}
                                        />
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                <button
                                    onClick={async () => {
                                        try {
                                            setModal({ isOpen: true, type: 'loading', title: 'Generating Metadata', message: 'Creating professional Korean YouTube metadata...', onConfirm: null });
                                            
                                            const response = await fetch(`${API_URL}/pipeline/generate-metadata`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ 
                                                    vibe: playlistConfig.vibe 
                                                })
                                            });
                                            
                                            if (response.ok) {
                                                const data = await response.json();
                                                setPlaylistMetadata({
                                                    title: data.title || '',
                                                    description: data.description || '',
                                                    timestamps: data.timestamps || []
                                                });
                                                setModal({ ...modal, isOpen: false });
                                            } else {
                                                const error = await response.json();
                                                setModal({ 
                                                    isOpen: true, 
                                                    type: 'error', 
                                                    title: 'Metadata Generation Failed', 
                                                    message: error.error || 'Failed to generate metadata',
                                                    onConfirm: () => setModal({ ...modal, isOpen: false })
                                                });
                                            }
                                        } catch (error) {
                                            console.error('Metadata generation error:', error);
                                            setModal({ 
                                                isOpen: true, 
                                                type: 'error', 
                                                title: 'Error', 
                                                message: 'Failed to generate metadata: ' + error.message,
                                                onConfirm: () => setModal({ ...modal, isOpen: false })
                                            });
                                        }
                                    }}
                                    style={{
                                        padding: '12px 30px',
                                        fontSize: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid #666',
                                        background: '#222',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        marginRight: '15px'
                                    }}
                                >
                                    🤖 Auto-Generate
                                </button>
                                <button
                                    onClick={() => setPlaylistStage('final-preview')}
                                    style={{
                                        padding: '15px 40px',
                                        fontSize: '1.1rem',
                                        borderRadius: '8px',
                                        border: 'none',
                                        background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                        color: '#fff',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                    }}
                                >
                                    Continue to Preview →
                                </button>
                            </div>
                                </div>
                            )}
                        </div>
                    )}

                    {playlistStage === 'final-preview' && (
                        <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '12px', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>👁️ Stage 8: Final Preview</h3>
                            <p style={{ color: '#888', marginBottom: '30px' }}>Review your video before uploading to YouTube</p>
                            
                            {/* YouTube-style preview */}
                            <div style={{ maxWidth: '1200px', margin: '0 auto', background: '#0f0f0f', borderRadius: '12px', overflow: 'hidden' }}>
                                {/* Video player */}
                                <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
                                    <video 
                                        controls 
                                        style={{ 
                                            position: 'absolute', 
                                            top: 0, 
                                            left: 0, 
                                            width: '100%', 
                                            height: '100%',
                                            objectFit: 'contain'
                                        }}
                                        src={`${API_URL}/temp/playlists/${sessionId}_playlist.mp4`}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                                
                                {/* Video info section (YouTube-style) */}
                                <div style={{ padding: '20px' }}>
                                    {/* Title */}
                                    <h2 style={{ 
                                        color: '#fff', 
                                        fontSize: '1.4rem', 
                                        fontWeight: '600',
                                        marginBottom: '12px',
                                        lineHeight: '1.4'
                                    }}>
                                        {playlistMetadata.title || 'Playlist Title'}
                                    </h2>
                                    
                                    {/* Stats bar */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px',
                                        color: '#aaa',
                                        fontSize: '0.9rem',
                                        marginBottom: '16px',
                                        paddingBottom: '16px',
                                        borderBottom: '1px solid #333'
                                    }}>
                                        <span>📊 Ready for upload</span>
                                        <span>•</span>
                                        <span>{playlistConfig.numSongs} songs</span>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{ 
                                        background: '#1a1a1a',
                                        padding: '16px',
                                        borderRadius: '8px',
                                        marginBottom: '20px'
                                    }}>
                                        <h4 style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '12px', fontWeight: '600' }}>Description</h4>
                                        <pre style={{ 
                                            color: '#ccc', 
                                            fontSize: '0.85rem',
                                            lineHeight: '1.6',
                                            whiteSpace: 'pre-wrap',
                                            wordWrap: 'break-word',
                                            fontFamily: 'inherit',
                                            margin: 0
                                        }}>
                                            {playlistMetadata.description || 'No description available'}
                                        </pre>
                                    </div>
                                    
                                    {/* Action buttons */}
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '30px' }}>
                                        <button
                                            onClick={() => setPlaylistStage('metadata')}
                                            style={{
                                                padding: '12px 30px',
                                                fontSize: '1rem',
                                                borderRadius: '8px',
                                                border: '1px solid #666',
                                                background: '#222',
                                                color: '#fff',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            ← Edit Metadata
                                        </button>
                                        <button
                                            onClick={() => setPlaylistStage('upload')}
                                            style={{
                                                padding: '15px 40px',
                                                fontSize: '1.1rem',
                                                borderRadius: '8px',
                                                border: 'none',
                                                background: 'linear-gradient(90deg, #FF9068 0%, #FF4B1F 100%)',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                boxShadow: '0 4px 15px rgba(255, 75, 31, 0.3)'
                                            }}
                                        >
                                            🚀 Upload to YouTube
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {playlistStage === 'upload' && (
                        <div style={{ background: '#1a1a1a', padding: '40px', borderRadius: '12px', textAlign: 'center', marginBottom: '80px' }}>
                            <h3 style={{ color: '#FF9068', marginBottom: '20px' }}>📤 Stage 9: Upload to YouTube</h3>
                            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🚀</div>
                            <div style={{ background: '#111', padding: '30px', borderRadius: '8px', marginBottom: '30px', textAlign: 'left', maxWidth: '600px', margin: '0 auto 30px' }}>
                                <h4 style={{ color: '#FF9068', marginTop: 0, marginBottom: '15px' }}>Final Confirmation</h4>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '10px' }}>✓ {playlistConfig.numSongs} songs generated</p>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '10px' }}>✓ Videos created with selected images</p>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '10px' }}>✓ Playlist concatenated and ready</p>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '10px' }}>✓ Metadata prepared</p>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '0' }}>✓ YouTube authentication connected</p>
                            </div>
                            <button
                                onClick={async () => {
                                    setModal({
                                        isOpen: true,
                                        type: 'confirm',
                                        title: 'Upload to YouTube?',
                                        message: 'Upload playlist to YouTube? This action cannot be undone.',
                                        showCancel: true,
                                        onConfirm: async () => {
                                            setModal({ isOpen: true, type: 'loading', title: 'Uploading', message: 'Uploading to YouTube...', onConfirm: null });
                                            // TODO: Call backend API
                                        },
                                        onClose: () => setModal({ ...modal, isOpen: false })
                                    });
                                }}
                                style={{
                                    padding: '18px 50px',
                                    fontSize: '1.2rem',
                                    borderRadius: '10px',
                                    border: 'none',
                                    background: 'linear-gradient(90deg, #1DB954 0%, #1ed760 100%)',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    boxShadow: '0 6px 20px rgba(29, 185, 84, 0.4)'
                                }}
                            >
                                📤 Upload to YouTube
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}

        {/* Configuration Tab */}
        {activeSubTab === 'config' && (
            <div style={{ marginTop: '20px', height: 'calc(100vh - 170px)', overflowY: 'auto', paddingRight: '10px', paddingBottom: '60px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', position: 'sticky', top: 0, background: '#0a0a0a', zIndex: 10, paddingBottom: '10px' }}>
                    <h2 style={{ color: '#FF9068', margin: 0 }}>⚙️ Configuration</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={saveConfig}
                            disabled={configLoading || !config}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#1DB954', 
                                border: 'none', 
                                borderRadius: '8px', 
                                color: '#fff', 
                                fontWeight: 'bold', 
                                cursor: configLoading ? 'not-allowed' : 'pointer',
                                opacity: configLoading ? 0.5 : 1
                            }}
                        >
                            {configLoading ? 'Saving...' : '💾 Save Configuration'}
                        </button>
                        <button 
                            onClick={resetConfig}
                            disabled={configLoading}
                            style={{ 
                                padding: '10px 20px', 
                                background: '#666', 
                                border: 'none', 
                                borderRadius: '8px', 
                                color: '#fff', 
                                fontWeight: 'bold', 
                                cursor: configLoading ? 'not-allowed' : 'pointer',
                                opacity: configLoading ? 0.5 : 1
                            }}
                        >
                            🔄 Reset to Defaults
                        </button>
                    </div>
                </div>

                {configLoading && !config ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <div className="spinner" style={{ margin: '0 auto 20px' }}></div>
                        <p>Loading configuration...</p>
                    </div>
                ) : config ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        
                        {/* Lyrics Generation Section */}
                        <div style={{ background: '#1a1a1a', padding: '20px', borderRadius: '12px', border: '1px solid #333' }}>
                            <h3 style={{ color: '#FF9068', marginTop: 0 }}>📝 Lyrics Generation (Mood-Based)</h3>
                            
                            {/* Energetic Mood */}
                            <div style={{ marginBottom: '25px', padding: '15px', background: '#0f0f0f', borderRadius: '8px', border: '1px solid #2a2a2a' }}>
                                <h4 style={{ color: '#4CAF50', marginTop: 0, marginBottom: '15px' }}>⚡ Energetic Mood</h4>
                                
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>System Prompt</label>
                                    <textarea
                                        value={config.lyrics_generation?.energetic?.system_prompt || ''}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    energetic: { ...prev.lyrics_generation.energetic, system_prompt: e.target.value }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            minHeight: '80px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Style Instructions</label>
                                    <textarea
                                        value={config.lyrics_generation?.energetic?.style_instructions || ''}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    energetic: { ...prev.lyrics_generation.energetic, style_instructions: e.target.value }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            minHeight: '60px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Banned Words (comma-separated)</label>
                                    <input
                                        type="text"
                                        value={config.lyrics_generation?.energetic?.banned_words?.join(', ') || ''}
                                        onChange={(e) => {
                                            const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    energetic: { ...prev.lyrics_generation.energetic, banned_words: words }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Temperature</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={config.lyrics_generation?.energetic?.temperature || 0.95}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    energetic: { ...prev.lyrics_generation.energetic, temperature: parseFloat(e.target.value) }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '150px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Sad Mood */}
                            <div style={{ marginBottom: '15px', padding: '15px', background: '#0f0f0f', borderRadius: '8px', border: '1px solid #2a2a2a' }}>
                                <h4 style={{ color: '#2196F3', marginTop: 0, marginBottom: '15px' }}>💧 Sad Mood</h4>
                                
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>System Prompt</label>
                                    <textarea
                                        value={config.lyrics_generation?.sad?.system_prompt || ''}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    sad: { ...prev.lyrics_generation.sad, system_prompt: e.target.value }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            minHeight: '80px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Style Instructions</label>
                                    <textarea
                                        value={config.lyrics_generation?.sad?.style_instructions || ''}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    sad: { ...prev.lyrics_generation.sad, style_instructions: e.target.value }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            minHeight: '60px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontFamily: 'monospace',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Banned Words (comma-separated)</label>
                                    <input
                                        type="text"
                                        value={config.lyrics_generation?.sad?.banned_words?.join(', ') || ''}
                                        onChange={(e) => {
                                            const words = e.target.value.split(',').map(w => w.trim()).filter(w => w);
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    sad: { ...prev.lyrics_generation.sad, banned_words: words }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '100%', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontSize: '0.85rem'
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Temperature</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={config.lyrics_generation?.sad?.temperature || 0.9}
                                        onChange={(e) => {
                                            setConfig(prev => ({
                                                ...prev,
                                                lyrics_generation: {
                                                    ...prev.lyrics_generation,
                                                    sad: { ...prev.lyrics_generation.sad, temperature: parseFloat(e.target.value) }
                                                }
                                            }));
                                        }}
                                        style={{ 
                                            width: '150px', 
                                            background: '#111', 
                                            border: '1px solid #444', 
                                            borderRadius: '6px', 
                                            color: '#eee', 
                                            padding: '10px',
                                            fontSize: '0.9rem'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Language */}
                            <div>
                                <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Language</label>
                                <input
                                    type="text"
                                    value={config.lyrics_generation?.language || ''}
                                    onChange={(e) => updateConfigField('lyrics_generation', 'language', e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        background: '#111', 
                                        border: '1px solid #444', 
                                        borderRadius: '6px', 
                                        color: '#eee', 
                                        padding: '10px',
                                        fontSize: '0.9rem'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Music Generation Section */}
                        <div style={{ background: '#1a1a1a', padding: '20px', borderRadius: '12px', border: '1px solid #333' }}>
                            <h3 style={{ color: '#FF9068', marginTop: 0 }}>🎵 Music Generation (Suno Style Strings)</h3>
                            <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-10px', marginBottom: '20px' }}>
                                These are the exact style strings sent to Suno for music generation
                            </p>
                            
                            <div>
                                {Object.entries(config.music_generation || {}).filter(([key]) => key !== 'vibe_mappings' && key !== 'default_style').map(([vibe, vibeConfig]) => {
                                    // Handle both old string format and new object format
                                    const styleValue = typeof vibeConfig === 'object' ? vibeConfig.style : vibeConfig;
                                    const negativeTags = typeof vibeConfig === 'object' ? vibeConfig.negative_tags : '';
                                    const styleWeight = typeof vibeConfig === 'object' ? vibeConfig.style_weight : 0.7;
                                    const weirdness = typeof vibeConfig === 'object' ? vibeConfig.weirdness_constraint : 0.4;
                                    const model = typeof vibeConfig === 'object' ? vibeConfig.model : 'V5';
                                    
                                    return (
                                        <div key={vibe} style={{ marginBottom: '25px', padding: '15px', background: '#111', borderRadius: '8px', border: '1px solid #333' }}>
                                            <label style={{ display: 'block', color: '#4CAF50', marginBottom: '10px', fontSize: '1rem', textTransform: 'capitalize', fontWeight: 'bold' }}>
                                                {vibe === 'energetic' ? '⚡' : vibe === 'sad' ? '💧' : vibe === 'emotional' ? '💙' : '🎵'} {vibe}
                                            </label>
                                            
                                            {/* Style String */}
                                            <label style={{ display: 'block', color: '#888', marginBottom: '5px', fontSize: '0.8rem' }}>Style Tags (genre, instruments, tempo, atmosphere)</label>
                                            <textarea
                                                value={styleValue || ''}
                                                onChange={(e) => {
                                                    setConfig(prev => ({
                                                        ...prev,
                                                        music_generation: {
                                                            ...prev.music_generation,
                                                            [vibe]: { ...prev.music_generation?.[vibe], style: e.target.value }
                                                        }
                                                    }));
                                                }}
                                                placeholder="Korean Trot, Ppong-jjak, 120 BPM, Accordion, Brass, Nostalgic..."
                                                style={{ 
                                                    width: '100%',
                                                    minHeight: '70px',
                                                    background: '#0a0a0a', 
                                                    border: '1px solid #333', 
                                                    borderRadius: '6px', 
                                                    color: '#eee', 
                                                    padding: '10px',
                                                    fontSize: '0.85rem',
                                                    fontFamily: 'monospace',
                                                    marginBottom: '10px'
                                                }}
                                            />
                                            
                                            {/* Negative Tags */}
                                            <label style={{ display: 'block', color: '#888', marginBottom: '5px', fontSize: '0.8rem' }}>Negative Tags (elements to EXCLUDE)</label>
                                            <input
                                                type="text"
                                                value={negativeTags || ''}
                                                onChange={(e) => {
                                                    setConfig(prev => ({
                                                        ...prev,
                                                        music_generation: {
                                                            ...prev.music_generation,
                                                            [vibe]: { ...prev.music_generation?.[vibe], negative_tags: e.target.value }
                                                        }
                                                    }));
                                                }}
                                                placeholder="rap, hip-hop, heavy metal, screaming..."
                                                style={{ 
                                                    width: '100%',
                                                    background: '#0a0a0a', 
                                                    border: '1px solid #333', 
                                                    borderRadius: '6px', 
                                                    color: '#eee', 
                                                    padding: '10px',
                                                    fontSize: '0.85rem',
                                                    marginBottom: '10px'
                                                }}
                                            />
                                            
                                            {/* Advanced Parameters Row */}
                                            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                                                {/* Style Weight */}
                                                <div style={{ flex: '1', minWidth: '120px' }}>
                                                    <label style={{ display: 'block', color: '#888', marginBottom: '5px', fontSize: '0.75rem' }}>Style Weight (0-1)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="1"
                                                        step="0.1"
                                                        value={styleWeight ?? 0.7}
                                                        onChange={(e) => {
                                                            setConfig(prev => ({
                                                                ...prev,
                                                                music_generation: {
                                                                    ...prev.music_generation,
                                                                    [vibe]: { ...prev.music_generation?.[vibe], style_weight: parseFloat(e.target.value) }
                                                                }
                                                            }));
                                                        }}
                                                        style={{ 
                                                            width: '100%',
                                                            background: '#0a0a0a', 
                                                            border: '1px solid #333', 
                                                            borderRadius: '6px', 
                                                            color: '#eee', 
                                                            padding: '8px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                </div>
                                                
                                                {/* Weirdness */}
                                                <div style={{ flex: '1', minWidth: '120px' }}>
                                                    <label style={{ display: 'block', color: '#888', marginBottom: '5px', fontSize: '0.75rem' }}>Weirdness (0-1)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="1"
                                                        step="0.1"
                                                        value={weirdness ?? 0.4}
                                                        onChange={(e) => {
                                                            setConfig(prev => ({
                                                                ...prev,
                                                                music_generation: {
                                                                    ...prev.music_generation,
                                                                    [vibe]: { ...prev.music_generation?.[vibe], weirdness_constraint: parseFloat(e.target.value) }
                                                                }
                                                            }));
                                                        }}
                                                        style={{ 
                                                            width: '100%',
                                                            background: '#0a0a0a', 
                                                            border: '1px solid #333', 
                                                            borderRadius: '6px', 
                                                            color: '#eee', 
                                                            padding: '8px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                </div>
                                                
                                                {/* Model */}
                                                <div style={{ flex: '1', minWidth: '120px' }}>
                                                    <label style={{ display: 'block', color: '#888', marginBottom: '5px', fontSize: '0.75rem' }}>Model</label>
                                                    <select
                                                        value={model || 'V5'}
                                                        onChange={(e) => {
                                                            setConfig(prev => ({
                                                                ...prev,
                                                                music_generation: {
                                                                    ...prev.music_generation,
                                                                    [vibe]: { ...prev.music_generation?.[vibe], model: e.target.value }
                                                                }
                                                            }));
                                                        }}
                                                        style={{ 
                                                            width: '100%',
                                                            background: '#0a0a0a', 
                                                            border: '1px solid #333', 
                                                            borderRadius: '6px', 
                                                            color: '#eee', 
                                                            padding: '8px',
                                                            fontSize: '0.85rem'
                                                        }}
                                                    >
                                                        <option value="V5">V5 (Best Expression)</option>
                                                        <option value="V4_5PLUS">V4.5+ (Richest Sound)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Tips Section */}
                            <div style={{ marginTop: '15px', padding: '15px', background: '#0d1117', borderRadius: '8px', border: '1px solid #30363d' }}>
                                <h4 style={{ color: '#58a6ff', margin: '0 0 10px 0', fontSize: '0.9rem' }}>💡 Pro Tips for Better Music</h4>
                                <ul style={{ color: '#8b949e', fontSize: '0.8rem', margin: 0, paddingLeft: '20px', lineHeight: '1.6' }}>
                                    <li><strong>Style Tags:</strong> Include BPM, specific instruments, atmosphere (e.g., "120 BPM, Accordion, Brass, Nostalgic")</li>
                                    <li><strong>Negative Tags:</strong> Exclude unwanted elements (e.g., "rap, electronic, heavy drums")</li>
                                    <li><strong>Style Weight:</strong> Higher (0.7-0.9) = stricter adherence to style</li>
                                    <li><strong>Weirdness:</strong> 0.3-0.5 for stable songs, 0.6+ for experimental</li>
                                    <li><strong>Model:</strong> V5 for speed, V4.5+ for richest sound quality</li>
                                </ul>
                            </div>
                        </div>

                        {/* Market Analysis Section */}
                        <div style={{ background: '#1a1a1a', padding: '20px', borderRadius: '12px', border: '1px solid #333', marginBottom: '30px' }}>
                            <h3 style={{ color: '#FF9068', marginTop: 0 }}>📊 Market Analysis (Agent 2)</h3>
                            <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-10px', marginBottom: '20px' }}>
                                Agent 2 learns from reference YouTube videos to understand what makes a hit song in your target market
                            </p>
                            
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', color: '#4CAF50', marginBottom: '10px', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                    🎯 Reference Market URL
                                </label>
                                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-5px', marginBottom: '10px' }}>
                                    YouTube video URL of hit songs in your target genre (e.g., Korean trot playlist). Agent 2 will analyze and use as baseline.
                                </p>
                                <input
                                    type="text"
                                    value={config.market_analysis?.reference_url || ''}
                                    onChange={(e) => {
                                        setConfig(prev => ({
                                            ...prev,
                                            market_analysis: {
                                                ...prev.market_analysis,
                                                reference_url: e.target.value
                                            }
                                        }));
                                    }}
                                    placeholder="https://youtube.com/watch?v=..."
                                    style={{ 
                                        width: '100%', 
                                        background: '#111', 
                                        border: '1px solid #444', 
                                        borderRadius: '6px', 
                                        color: '#eee', 
                                        padding: '12px',
                                        fontSize: '0.9rem',
                                        fontFamily: 'monospace'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <button
                                    onClick={async () => {
                                        if (!config.market_analysis?.reference_url) {
                                            setModal({ isOpen: true, type: 'warning', title: 'Missing URL', message: 'Please enter a YouTube URL first', onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                            return;
                                        }
                                        
                                        setModal({
                                            isOpen: true,
                                            type: 'confirm',
                                            title: 'Analyze Reference Video?',
                                            message: 'Analyze this YouTube video and update Agent 2 baseline? This may take a few minutes.',
                                            showCancel: true,
                                            onConfirm: async () => {
                                                setModal({ isOpen: true, type: 'loading', title: 'Analyzing Video', message: 'Downloading and analyzing audio features...\nThis may take 2-3 minutes.', onConfirm: null });
                                                
                                                try {
                                                    const res = await fetch(`${API_URL}/market/analyze-reference`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ url: config.market_analysis.reference_url })
                                                    });
                                                    const data = await res.json();
                                                    if (res.ok) {
                                                        setModal({ isOpen: true, type: 'success', title: 'Analysis Complete', message: '✅ Reference analysis complete! Agent 2 will now use this as baseline.', onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                        await fetchConfig(); // Refresh config to show last_analyzed timestamp
                                                    } else {
                                                        setModal({ isOpen: true, type: 'error', title: 'Analysis Failed', message: (data.detail || data.error || 'Unknown error'), onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                    }
                                                } catch (err) {
                                                    setModal({ isOpen: true, type: 'error', title: 'Error', message: err.message, onConfirm: () => setModal({ ...modal, isOpen: false }) });
                                                }
                                            },
                                            onClose: () => setModal({ ...modal, isOpen: false })
                                        });
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        background: '#2196F3',
                                        border: 'none',
                                        borderRadius: '6px',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    🔍 Analyze Reference Video
                                </button>
                                {config.market_analysis?.last_analyzed && (
                                    <p style={{ color: '#4CAF50', fontSize: '0.85rem', marginTop: '10px', marginBottom: 0 }}>
                                        ✓ Last analyzed: {new Date(config.market_analysis.last_analyzed).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Image Generation Section */}
                        <div style={{ background: '#1a1a1a', padding: '20px', borderRadius: '12px', border: '1px solid #333', marginBottom: '40px' }}>
                            <h3 style={{ color: '#FF9068', marginTop: 0 }}>🎨 Image Generation</h3>
                            
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', color: '#4CAF50', marginBottom: '10px', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                    📖 Playlist Cover Template
                                </label>
                                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-5px', marginBottom: '10px' }}>
                                    For final playlist video - includes title and numbered song list
                                </p>
                                <textarea
                                    value={config.image_generation?.playlist_cover_template || ''}
                                    onChange={(e) => updateConfigField('image_generation', 'playlist_cover_template', e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        minHeight: '120px', 
                                        background: '#111', 
                                        border: '1px solid #444', 
                                        borderRadius: '6px', 
                                        color: '#eee', 
                                        padding: '10px',
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', color: '#2196F3', marginBottom: '10px', fontSize: '0.95rem', fontWeight: 'bold' }}>
                                    🎵 Single Song Template
                                </label>
                                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '-5px', marginBottom: '10px' }}>
                                    For individual song thumbnails - use {'{title}'} and {'{lyrics}'} placeholders
                                </p>
                                <textarea
                                    value={config.image_generation?.single_song_template || config.image_generation?.prompt_template || ''}
                                    onChange={(e) => updateConfigField('image_generation', 'single_song_template', e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        minHeight: '80px', 
                                        background: '#111', 
                                        border: '1px solid #444', 
                                        borderRadius: '6px', 
                                        color: '#eee', 
                                        padding: '10px',
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', color: '#aaa', marginBottom: '5px', fontSize: '0.9rem' }}>Number of Candidates</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="4"
                                    value={config.image_generation?.num_candidates || 4}
                                    onChange={(e) => updateConfigField('image_generation', 'num_candidates', parseInt(e.target.value))}
                                    style={{ 
                                        width: '150px', 
                                        background: '#111', 
                                        border: '1px solid #444', 
                                        borderRadius: '6px', 
                                        color: '#eee', 
                                        padding: '10px',
                                        fontSize: '0.9rem'
                                    }}
                                />
                                <span style={{ color: '#666', fontSize: '0.8rem', marginLeft: '10px' }}>
                                    (1-4 images per song)
                                </span>
                            </div>
                        </div>

                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <p>Failed to load configuration</p>
                    </div>
                )}
            </div>
        )}

        {/* Session Modal */}
        {showSessionModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#1a1a1a', padding: '30px', borderRadius: '16px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto', border: '1px solid #333' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, color: '#FF9068' }}>📂 Past Sessions</h2>
                        <button onClick={() => setShowSessionModal(false)} style={{ background: 'transparent', border: 'none', color: '#888', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
                    </div>
                    
                    {sessions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <p>No past sessions found.</p>
                            <p style={{ fontSize: '0.9rem' }}>Create songs and they'll be saved in sessions you can load later.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {sessions.map(session => (
                                <div key={session.session_id} style={{ background: '#111', padding: '15px', borderRadius: '8px', border: '1px solid #222', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: '#eee', marginBottom: '5px' }}>{session.name}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                            {session.actual_song_count} songs • {new Date(session.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => loadSession(session.session_id)}
                                        style={{ 
                                            padding: '8px 16px', 
                                            background: '#FF9068', 
                                            border: 'none', 
                                            borderRadius: '6px', 
                                            color: '#000', 
                                            fontWeight: 'bold', 
                                            cursor: 'pointer' 
                                        }}
                                    >
                                        Load
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )}

        <style>{`
            .spinner {
                width: 18px;
                height: 18px;
                border: 2px solid rgba(255,255,255,0.3);
                border-radius: 50%;
                border-top-color: #fff;
                animation: spin 0.8s linear infinite;
            }
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            .blink-cursor {
                display: inline-block;
                animation: blink 1s step-end infinite;
                color: #1DB954;
            }
            @keyframes blink {
                0%, 50% { opacity: 1; }
                51%, 100% { opacity: 0; }
            }
            
            /* Smooth animations for song cards */
            .jury-container > div > div > div > div {
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                animation: slideIn 0.3s ease-out;
            }
            
            @keyframes slideIn {
                from {
                    opacity: 0;
                    transform: translateY(-10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            /* Button hover effects */
            .jury-container button {
                transition: all 0.2s ease;
            }
            
            .jury-container button:hover {
                transform: scale(1.05);
                filter: brightness(1.2);
            }
            
            .jury-container button:active {
                transform: scale(0.95);
            }
        `}</style>
        
        {/* Global Modal */}
        <Modal 
            isOpen={modal.isOpen}
            onClose={() => setModal({ ...modal, isOpen: false })}
            title={modal.title}
            message={modal.message}
            type={modal.type}
            onConfirm={modal.onConfirm}
            showCancel={modal.showCancel}
        />
    </div>
);
};

export default GenPro;
